/* eslint-disable no-empty */
import { bus } from "@/main";
import imageCompression from "browser-image-compression";
import { getOrCreateDeviceToken } from "@/hooks/deviceToken";
const BASE_URL =
  process.env.VUE_APP_TTS_BASE_URL ||
  "https://api.oneappgo.com/bd702088a7598f6846c7ca3dfe082ec3/tts";
const QS_BASE_URL =
  "https://api.oneappgo.com/bd702088a7598f6846c7ca3dfe082ec3/qs";
const POS_REQUEST_BASE_URL = process.env.VUE_APP_POS_REQUEST_BASE_URL || "";
const POS_API_BASE_URL = process.env.VUE_APP_POS_BASE_URL || `${BASE_URL}/pos`;
const resolvePosRequestRawUrl = (path) => {
  const cleanBase = `${POS_REQUEST_BASE_URL}`.trim().replace(/\/+$/, "");
  if (!cleanBase) return undefined;
  return `${cleanBase}/${path}`;
};
const resolvePosApiRawUrl = (path) => {
  const cleanBase = `${POS_API_BASE_URL}`.trim().replace(/\/+$/, "");
  if (!cleanBase) return undefined;
  return `${cleanBase}/${path}`;
};
const buildPosFormData = (context, data = {}) => {
  const formData = new FormData();
  formData.append("sesscode", context.getters.getUser.sesscode);
  formData.append("userid", context.getters.getUser.userid);
  formData.append("businessid", context.getters.getUser.business_id);
  formData.append("env", "web");
  formData.append("platform", "bolddweb");
  const transpin =
    context.getters.getUser?.trans_pin ||
    context.getters.getUser?.transpin ||
    context.getters.getUser?.tpin ||
    context.getters.getUser?.pin ||
    "";
  if (transpin) {
    formData.append("transpin", transpin);
  }
  Object.keys(data).forEach((key) => {
    const value = data[key];
    if (value === undefined || value === null) return;
    formData.append(key, value);
  });
  return formData;
};
const sanitizeFetchErrorText = (value) =>
  String(value || "")
    .replace(/<[^>]*>/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .slice(0, 220);
const buildFetchFallbackResponse = (message, status = 0, rawText = "") => ({
  ok: false,
  status,
  json: async () => ({
    status: false,
    message,
    msg: message,
  }),
  text: async () => rawText || message,
});
const tryParseFetchJson = (rawText = "") => {
  if (!rawText) return undefined;
  try {
    return JSON.parse(rawText);
  } catch (e) {
    return undefined;
  }
};
const extractFilenameFromDisposition = (value = "", fallbackName = "download") => {
  const filenameStarMatch = /filename\*\s*=\s*UTF-8''([^;]+)/i.exec(value);
  if (filenameStarMatch?.[1]) {
    try {
      return decodeURIComponent(filenameStarMatch[1]).replace(/["']/g, "");
    } catch (e) {
      return filenameStarMatch[1].replace(/["']/g, "");
    }
  }

  const filenameMatch = /filename\s*=\s*"?([^";]+)"?/i.exec(value);
  if (filenameMatch?.[1]) {
    return filenameMatch[1].trim();
  }

  return fallbackName;
};
const safeJsonOrFileFetch = async (
  url,
  options,
  fallbackMessage,
  fallbackFilename = "download"
) => {
  try {
    const response = await fetch(url, options);
    const contentType = `${response.headers.get("content-type") || ""}`.toLowerCase();
    const disposition = `${response.headers.get("content-disposition") || ""}`.toLowerCase();
    const isDirectFileResponse =
      disposition.includes("attachment") ||
      /(application\/pdf|application\/zip|application\/octet-stream|application\/vnd\.ms-excel|application\/vnd\.openxmlformats-officedocument\.spreadsheetml\.sheet|text\/csv)/i.test(
        contentType
      );

    if (isDirectFileResponse) {
      const blob = await response.blob();
      const filename = extractFilenameFromDisposition(
        response.headers.get("content-disposition") || "",
        fallbackFilename
      );

      return {
        ok: response.ok,
        status: response.status,
        json: async () => ({
          status: response.ok,
          message: response.ok
            ? "Statement export is ready for download"
            : fallbackMessage,
          download_blob: blob,
          download_name: filename,
          direct_download: response.ok,
        }),
        text: async () => "",
      };
    }

    return {
      ok: response.ok,
      status: response.status,
      json: async () => {
        try {
          return await response.clone().json();
        } catch (e) {
          const rawText = await response.text().catch(() => "");
          const parsed = tryParseFetchJson(rawText);
          if (parsed) return parsed;
          return {
            status: false,
            message: sanitizeFetchErrorText(rawText) || fallbackMessage,
          };
        }
      },
      text: async () => response.text(),
    };
  } catch (e) {
    return buildFetchFallbackResponse(
      e.message || fallbackMessage,
      0,
      sanitizeFetchErrorText(e.message || "")
    );
  }
};
const normalizeBankAccountRecord = (record, currency) => {
  const item = record && typeof record === "object" ? record : {};
  const resolvedCurrency = String(item.currency || currency || "USD").toUpperCase();

  return {
    ...item,
    id: item.id || item.account_number || `${resolvedCurrency}-${item.account_name || item.bank_name || "default"}`,
    currency: resolvedCurrency,
    bankname: item.bank_name || item.bankname || item.bank || "",
    accountnumber: item.account_number || item.accountnumber || item.accountno || "",
    acctname: item.account_holder || item.acctname || item.account_name || "",
    routingnumber: item.routing_number || item.routingnumber || item.sort_code || "",
    source: item.source || "company",
    label: item.account_name || item.account_holder || item.bank_name || item.label || "",
    metadata: {
      ...(item.metadata || {}),
      bank_address: item.bank_address || item.metadata?.bank_address || "",
      swift_bic: item.swift_bic || item.metadata?.swift_bic || "",
      payment_rail: item.payment_rail || item.metadata?.payment_rail || "",
      transfer_type_domestic: item.transfer_type_domestic || item.metadata?.transfer_type_domestic || "",
      transfer_type_international:
        item.transfer_type_international || item.metadata?.transfer_type_international || "",
    },
  };
};
const MAX_VERIFICATION_PDF_BYTES = 2 * 1024 * 1024;
const MAX_VERIFICATION_IMAGE_UPLOAD_BYTES = 5 * 1024 * 1024;
const MAX_VERIFICATION_IMAGE_BYTES = 500 * 1024;
const MAX_VERIFICATION_IMAGE_FALLBACK_BYTES = 2 * 1024 * 1024;
const MAX_VERIFICATION_IMAGE_MB = Number(
  (MAX_VERIFICATION_IMAGE_BYTES / (1024 * 1024)).toFixed(3)
);
const isBrowserFile = (value) => typeof File !== "undefined" && value instanceof File;
const fileNameMatches = (file, pattern) => pattern.test(String(file?.name || ""));
const isPdfFile = (file) =>
  isBrowserFile(file) &&
  (String(file.type || "").toLowerCase() === "application/pdf" ||
    fileNameMatches(file, /\.pdf$/i));
const isImageFile = (file) =>
  isBrowserFile(file) &&
  String(file.type || "").toLowerCase().startsWith("image/");
const withFileExtension = (fileName, extension) => {
  const baseName = String(fileName || `verification-${Date.now()}`).replace(
    /\.[^/.]+$/,
    ""
  );
  return `${baseName}.${extension}`;
};
const normalizeCompressedImageFile = (originalFile, compressedFile, fileType) => {
  const isBlob =
    typeof Blob !== "undefined" && compressedFile instanceof Blob;
  if (!isBrowserFile(compressedFile) && !isBlob) return originalFile;

  const resolvedType =
    fileType || compressedFile.type || originalFile.type || "image/jpeg";
  const extensionMap = {
    "image/jpeg": "jpg",
    "image/jpg": "jpg",
    "image/png": "png",
    "image/webp": "webp",
  };

  if (
    isBrowserFile(compressedFile) &&
    compressedFile.type === resolvedType &&
    compressedFile.name
  ) {
    return compressedFile;
  }

  return new File(
    [compressedFile],
    withFileExtension(originalFile.name, extensionMap[resolvedType] || "jpg"),
    {
      type: resolvedType,
      lastModified: Date.now(),
    }
  );
};
const compressVerificationImage = async (file) => {
  if (!isImageFile(file) || file.size <= MAX_VERIFICATION_IMAGE_BYTES) return file;
  if (file.size > MAX_VERIFICATION_IMAGE_UPLOAD_BYTES) {
    throw new Error("Images must be 5MB or less before upload.");
  }

  const baseOptions = {
    maxSizeMB: MAX_VERIFICATION_IMAGE_MB,
    useWebWorker: true,
    initialQuality: 0.92,
    maxIteration: 15,
  };
  const attempts = [
    {
      ...baseOptions,
      maxWidthOrHeight: 2200,
      fileType: file.type || undefined,
    },
    {
      ...baseOptions,
      maxWidthOrHeight: 1800,
      fileType: file.type || undefined,
      initialQuality: 0.88,
    },
    {
      ...baseOptions,
      maxWidthOrHeight: 1800,
      fileType: "image/jpeg",
      initialQuality: 0.9,
    },
    {
      ...baseOptions,
      maxWidthOrHeight: 1400,
      fileType: "image/jpeg",
      initialQuality: 0.84,
    },
    {
      ...baseOptions,
      maxWidthOrHeight: 1200,
      fileType: "image/jpeg",
      initialQuality: 0.78,
    },
  ];

  let smallestFile = file;

  for (const options of attempts) {
    try {
      const compressed = await imageCompression(file, options);
      const normalized = normalizeCompressedImageFile(
        file,
        compressed,
        options.fileType
      );

      if (normalized.size < smallestFile.size) {
        smallestFile = normalized;
      }
      if (normalized.size <= MAX_VERIFICATION_IMAGE_BYTES) {
        return normalized;
      }
    } catch (e) {
      console.log(e.message);
    }
  }

  if (smallestFile.size <= MAX_VERIFICATION_IMAGE_FALLBACK_BYTES) {
    return smallestFile;
  }

  throw new Error(
    "Image could not be reduced below 2MB. Please upload a smaller image."
  );
};
const prepareVerificationUploadFile = async (file) => {
  if (!isBrowserFile(file)) {
    throw new Error("Document is required.");
  }

  if (isPdfFile(file)) {
    if (file.size > MAX_VERIFICATION_PDF_BYTES) {
      throw new Error("PDF documents must be 2MB or less.");
    }
    return file;
  }

  if (isImageFile(file)) {
    return await compressVerificationImage(file);
  }

  return file;
};
const readStoredRegUser = () => {
  if (typeof window === "undefined" || !window.localStorage) return undefined;
  try {
    const raw = window.localStorage.getItem("reg-user");
    if (!raw) return undefined;
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : undefined;
  } catch (e) {
    return undefined;
  }
};
const persistRegUser = (value) => {
  if (typeof window === "undefined" || !window.localStorage) return;
  if (!value || typeof value !== "object") {
    window.localStorage.removeItem("reg-user");
    return;
  }
  window.localStorage.setItem("reg-user", JSON.stringify(value));
};
const facilitySummaryCacheKey = (user = {}) => {
  const uid = `${user?.userid || user?.id || ""}`.trim();
  const bid = `${user?.business_id || user?.businessid || ""}`.trim();
  if (!uid && !bid) return "";
  return `facility-summary-cache-${uid || "guest"}-${bid || "none"}`;
};
const readFacilitySummaryCache = (user = {}) => {
  if (typeof window === "undefined" || !window.localStorage) return undefined;
  try {
    const key = facilitySummaryCacheKey(user);
    if (!key) return undefined;
    const raw = window.localStorage.getItem(key);
    if (!raw) return undefined;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return undefined;
    return {
      data:
        parsed.data && typeof parsed.data === "object" ? parsed.data : {},
      feature:
        parsed.feature && typeof parsed.feature === "object"
          ? parsed.feature
          : undefined,
    };
  } catch (e) {
    return undefined;
  }
};
const persistFacilitySummaryCache = (user = {}, data = {}, feature) => {
  if (typeof window === "undefined" || !window.localStorage) return;
  try {
    const key = facilitySummaryCacheKey(user);
    if (!key) return;
    const payload = {
      data: data && typeof data === "object" ? data : {},
      feature:
        feature && typeof feature === "object" ? feature : undefined,
      saved_at: Date.now(),
    };
    window.localStorage.setItem(key, JSON.stringify(payload));
  } catch (e) {
    //
  }
};
const clearFacilitySummaryCache = (user = {}) => {
  if (typeof window === "undefined" || !window.localStorage) return;
  try {
    const key = facilitySummaryCacheKey(user);
    if (!key) return;
    window.localStorage.removeItem(key);
  } catch (e) {
    //
  }
};
const resolveRegOnboardingMeta = (state, getters) => {
  const current =
    getters?.getRegUser && typeof getters.getRegUser === "object"
      ? getters.getRegUser
      : state?.regUser && typeof state.regUser === "object"
        ? state.regUser
        : readStoredRegUser();
  if (!current || typeof current !== "object") return {};
  return {
    ...current,
    business_id:
      current.business_id || current.businessid || current.newbizid || "",
    userid: current.userid || current.userid_ref || current.id || "",
    sesscode:
      current.sesscode ||
      current.sessioncode ||
      current.session_id ||
      getters?.getUser?.sesscode ||
      "",
  };
};
const normalizeVerificationStatusCode = (value) => {
  const parsed = parseInt(value, 10);
  return Number.isNaN(parsed) ? 0 : parsed;
};
const mapVerificationStatusToLegacyCache = (verification = {}) => {
  const documents = verification?.documents || {};
  return {
    idcard_status: normalizeVerificationStatusCode(documents?.idcard?.status),
    idcard_back: normalizeVerificationStatusCode(documents?.idcard_back?.status),
    utility_status: normalizeVerificationStatusCode(
      documents?.utilitybill?.status ?? documents?.utility?.status
    ),
    bizutility_status: normalizeVerificationStatusCode(
      documents?.biz_utility?.status
    ),
    cacfile_status: normalizeVerificationStatusCode(documents?.cacfile?.status),
    form8_status: normalizeVerificationStatusCode(documents?.cacform8?.status),
    statement_status: normalizeVerificationStatusCode(
      documents?.bankstatement?.status
    ),
    director_addrstatus: verification?.addressVerified ? 1 : 0,
    bvnver: verification?.bvnVerified ? 1 : 0,
    ninver: verification?.ninVerified ? 1 : 0,
    compliance_required: normalizeVerificationStatusCode(
      verification?.compliance_required
    ),
    compliance_pending: normalizeVerificationStatusCode(
      verification?.compliance_pending
    ),
    compliance_pending_reason: verification?.compliance_pending_reason || "",
    compliance_message: verification?.compliance_message || "",
    location_required: normalizeVerificationStatusCode(
      verification?.location_required
    ),
    location_grace_days: normalizeVerificationStatusCode(
      verification?.location_grace_days
    ),
    location_mode: verification?.location_mode || "while_in_use",
    latest_location_point: verification?.latest_location_point || null,
  };
};
const mergeVerificationIntoUserDetails = (userDetails, verification = {}) => {
  if (!userDetails || typeof userDetails !== "object") return userDetails;
  const legacy = mapVerificationStatusToLegacyCache(verification);
  const business = verification?.business || {};
  const identityVerified = !!(verification?.bvnVerified || verification?.ninVerified);
  const currentIdentityFlag = String(
    userDetails?.isverified ?? userDetails?.is_verified ?? ""
  );
  const nextIdentityFlag = identityVerified ? "2" : currentIdentityFlag;
  const businessAccountType = String(
    business?.current_account_type ??
      business?.account_type ??
      userDetails?.current_account_type ??
      userDetails?.account_type ??
      ""
  ).trim();
  const businessNextType = String(business?.next_type ?? "").trim();
  return {
    ...userDetails,
    current_account_type: businessAccountType || userDetails?.current_account_type,
    account_type: businessAccountType || userDetails?.account_type,
    business_account_type: businessAccountType,
    business_next_type: businessNextType,
    business_request_required: !!business?.request_required,
    business_legal_search_fee: business?.legal_search_fee ?? userDetails?.business_legal_search_fee ?? "",
    business_legal_search_currency: business?.currency ?? userDetails?.business_legal_search_currency ?? "NGN",
    idcard_status: String(legacy.idcard_status),
    idcard_back: String(legacy.idcard_back),
    utility_status: String(legacy.utility_status),
    bizutility_status: String(legacy.bizutility_status),
    cacfile_status: String(legacy.cacfile_status),
    form8_status: String(legacy.form8_status),
    statement_status: String(legacy.statement_status),
    director_addrstatus: String(legacy.director_addrstatus),
    bvnver: String(legacy.bvnver),
    ninver: String(legacy.ninver),
    isverified: nextIdentityFlag,
    is_verified: nextIdentityFlag,
  };
};
const safeJsonFetch = async (url, options, fallbackMessage) => {
  try {
    const response = await fetch(url, options);
    return {
      ok: response.ok,
      status: response.status,
      json: async () => {
        try {
          return await response.clone().json();
        } catch (e) {
          const rawText = sanitizeFetchErrorText(await response.text().catch(() => ""));
          return {
            status: false,
            message: rawText || fallbackMessage,
          };
        }
      },
      text: async () => response.text(),
    };
  } catch (e) {
    return buildFetchFallbackResponse(
      e.message || fallbackMessage,
      0,
      sanitizeFetchErrorText(e.message || "")
    );
  }
};
const user = {
  state: {
    regPage: 1,
    regUser: readStoredRegUser(),
    inviteCheck: undefined,
    user: "",
    userId: null,
    userDetails: "",
    passwordResetContext: null,
    currency: "NGN",
    banks: "",
    transactions: "",
    invoice: "",
    customers: "",
    state: "",
    country: "",
    city: "",
    payments: "",
    dataPlans: "",
    transaction: "",
    airtimePrices: "",
    cableTv: "",
    teamMembers: "",
    webHook: "",
    outflow: "",
    inflow: "",
    paymentLink: "",
    paymentLinkTransaction: "",
    businessInfo: "",
    checkOutDetails: "",
    verifyTransaction: "",
    auditLog: "",
    auditDetail: "",
    initializeTransfer: "",
    timer: "",
    oldTransfer: "",
    legderTransactions: "",
    ledgerTransDetails: "",
    gateways: "",
    payaccount: "",
    payCountry: null,
    payout: "",
    payoutDetail: "",
    disputes: "",
    bankAccounts: "",
    referrer: "",
    twoFactor: null,
    beneficiary: "",
    schedule: "",
    employee: "",
    scheduleHistory: "",
    customerdetails: "",
    currentCustomer: "",
    bulkpaymentBreakdown: "",
    cacreg: "",
    account_statement: "",
    businessBanks: "",
    cashiertransactions: "",
    branchUsers: "",
    branchSalesChart: "",
    branchTransactions: "",
    teamBranchSales: "",
    tvlist: "",
    cabletvplans: "",
    electricityProvider: "",
    branchStaff: "",
    branchDetails: "",
    payroll: "",
    payrollDetails: "",
    cashierBranchTrans: "",
    alertSettings: "",
    teamList: "",
    pendingTeamList: "",
    prodcategories: "",
    products: "",
    storeOrders: "",
    posAgents: "",
    posTerminals: "",
    posDash: "",
    posTransactions: "",
    bizFees: "",
    successMsg: null,
    verificationStatus: {},

    //pricing
    defaultSubPrice: undefined,
    premiumSubPrice: undefined,

    //cache
    caches: {
      cards: undefined,
      mywallet: undefined,
      accountno: undefined,
      branches: undefined,
      branchSales: undefined,
      dailygraph: undefined,
      branchgraph: undefined,
      subaccounts: undefined,
      payoutOffer: undefined,
      loanhistory: undefined,
      facilitySummary: undefined,
      facilityDetails: undefined,
      facilityFeature: undefined,
      translimit: undefined,
      myevents: undefined,
      selectedEvent: undefined,
      eventtickets: undefined,
      posTerminalAccess: undefined,
      posTerminalRequests: undefined,
    },
  },
  getters: {
    getCache: (state) => state.caches,
    getRegPage: (state) => state.regPage,
    getRegUser: (state) => state.regUser,
    getInviteCheck: (state) => state.inviteCheck,
    getUser: (state) => state.user,
    getUserId: (state) => state.userId,
    getUserDetails: (state) => state.userDetails,
    getPasswordResetContext: (state) => state.passwordResetContext,
    getBankDetails: (state) => state.banks,
    getTransactions: (state) => state.transactions,
    getInvoice: (state) => state.invoice,
    getCustomers: (state) => state.customers,
    getState: (state) => state.state,
    getCountry: (state) => state.country,
    getCity: (state) => state.city,
    getPayments: (state) => state.payments,
    getDataPlans: (state) => state.dataPlans,
    getTransactionDetail: (state) => state.transaction,
    getAirTimePrices: (state) => state.airtimePrices,
    getCableTv: (state) => state.cableTv,
    getTeamMembers: (state) => state.teamMembers,
    getWebHook: (state) => state.webHook,
    getInflow: (state) => state.inflow,
    getOutflow: (state) => state.outflow,
    getPaymentLink: (state) => state.paymentLink,
    getPaymentLinkTransaction: (state) => state.paymentLinkTransaction,
    getBusinessInfo: (state) => state.businessInfo,
    getCheckOutDetails: (state) => state.checkOutDetails,
    getVerifiedTransaction: (state) => state.verifyTransaction,
    getAuditLog: (state) => state.auditLog,
    getAuditDetail: (state) => state.auditDetail,
    getIntializeTransfer: (state) => state.initializeTransfer,
    getTransferTime: (state) => state.timer,
    getOldTransfer: (state) => state.oldTransfer,
    getLedgerTransaction: (state) => state.legderTransactions,
    getLedgerTransDetails: (state) => state.ledgerTransDetails,
    getGateways: (state) => state.gateways,
    getPayAccounts: (state) => state.payaccount,
    getPayCountry: (state) => state.payCountry,
    getPayout: (state) => state.payout,
    getPayoutDetail: (state) => state.payoutDetail,
    getDisputes: (state) => state.disputes,
    getBankAccounts: (state) => state.bankAccounts,
    getReferrer: (state) => state.referrer,
    getTwoFactor: (state) => state.twoFactor,
    getBeneficiary: (state) => state.beneficiary,
    getCurrency: (state) => state.currency,
    getSchedule: (state) => state.schedule,
    getEmployee: (state) => state.employee,
    getScheduleHistory: (state) => state.scheduleHistory,
    getCustomerDetails: (state) => state.customerdetails,
    getSubAcctDetails: (state) => state.customerdetails,
    getCurrentCustomer: (state) => state.currentCustomer,
    getBulkPaymentBreakdown: (state) => state.bulkpaymentBreakdown,
    getCacReg: (state) => state.cacreg,
    getAccountStatement: (state) => state.account_statement,
    getBusinessBanks: (state) => state.businessBanks,
    getCashierTransactions: (state) => state.cashiertransactions,
    getBranchSalesChart: (state) => state.branchSalesChart,
    getBranchUsers: (state) => state.branchUsers,
    getBranchTransactions: (state) => state.branchTransactions,
    getTeamBranchSales: (state) => state.teamBranchSales,
    getTvList: (state) => state.tvlist,
    getElectricityProvider: (state) => state.electricityProvider,
    getBranchStaff: (state) => state.branchStaff,
    getBranchDetails: (state) => state.branchDetails,
    getPayroll: (state) => state.payroll,
    getPayrollDetails: (state) => state.payrollDetails,
    getCashierBranchTrans: (state) => state.cashierBranchTrans,
    getAlertSettings: (state) => state.alertSettings,
    getTeamList: (state) => state.teamList,
    getPendingTeamList: (state) => state.pendingTeamList,
    getProdCategoriies: (state) => state.prodcategories,
    getProducts: (state) => state.products,
    getStoreOrder: (state) => state.storeOrders,
    getAgents: (state) => state.posAgents,
    getTerminals: (state) => state.posTerminals,
    getPOSDash: (state) => state.posDash,
    getPOSTransactions: (state) => state.posTransactions,
    getBizFees: (state) => state.bizFees,
    getSuccessMsg: (state) => state.successMsg,
    getVerificationStatus: (state) => state.verificationStatus,
    getDefaultPrices: (state) => state.defaultSubPrice,
    getPremiumPrices: (state) => state.premiumSubPrice,
  },
  mutations: {
    //update reg page
    updateRegPage: (state, details) => (state.regPage = details),
    updateRegUser: (state, details) => {
      state.regUser = details;
      persistRegUser(details);
    },

    //invite check
    updateInviteCheck: (state, details) => (state.inviteCheck = details),

    //update user
    updateUser: (state, details) => {
      const previousBusinessId = `${state?.user?.business_id || ""}`;
      const previousUserId = `${state?.user?.userid || state?.user?.id || ""}`;
      const nextBusinessId = `${details?.business_id || previousBusinessId || ""}`;

      state.user = details;

      if (previousBusinessId !== nextBusinessId) {
        clearFacilitySummaryCache({
          userid: previousUserId,
          business_id: previousBusinessId,
        });
        state.caches.facilitySummary = undefined;
        state.caches.facilityDetails = undefined;
        state.caches.facilityFeature = undefined;
      }
    },

    updateUserId: (state, details) => (state.userId = details),

    //update user business details
    updateUserDetails: (state, details) => (state.userDetails = details),

    setPasswordResetContext: (state, details) =>
      (state.passwordResetContext = details || null),
    clearPasswordResetContext: (state) => (state.passwordResetContext = null),

    updateVerificationStatus: (state, details) =>
      (state.verificationStatus = details || {}),

    //update bank details
    updateBankDetails: (state, details) => (state.banks = details),

    //update transactions
    updateTransactions: (state, data) => (state.transactions = data),

    //update invoice
    updateInvoice: (state, data) => (state.invoice = data),

    //update customers
    updateCustomers: (state, data) => (state.customers = data),

    //update state
    updateState: (state, data) => (state.state = data),

    //update country
    updateCountry: (state, data) => (state.country = data),

    //update city
    updateCity: (state, data) => (state.city = data),

    //update payment
    updatePayments: (state, data) => (state.payments = data),

    //update data plans
    updateDataPlans: (state, data) => (state.dataPlans = data),

    //update transaction detail
    updateTransactionDetail: (state, data) => (state.transaction = data),

    //update Airtime
    updateAirtimePrice: (state, data) => (state.airtimePrices = data),

    //update cable tv
    updateCableTV: (state, data) => (state.cableTv = data),

    //update team members
    updateTeamMembers: (state, data) => (state.teamMembers = data),

    //update web hook
    updateWebHook: (state, data) => (state.webHook = data),

    //update inflow
    updateInflow: (state, data) => (state.inflow = data),

    //update outflow
    updateOutflow: (state, data) => (state.outflow = data),

    //update payment link
    updatePaymentLink: (state, data) => (state.paymentLink = data),

    //update payment link transaction
    updatePaymentLinkTransaction: (state, data) =>
      (state.paymentLinkTransaction = data),

    //update business info
    updateBusinessInfo: (state, data) => (state.businessInfo = data),

    //update payment checkout details
    updateCheckOutDetails: (state, data) => (state.checkOutDetails = data),

    //update verified transaction
    updateVerifiedTransaction: (state, data) =>
      (state.verifyTransaction = data),

    //update audit log
    updateAuditLog: (state, data) => (state.auditLog = data),

    //update audit detail
    updateAuditLogDetail: (state, data) => (state.auditDetail = data),

    //update one app transfer account details
    updateInitializeTransfer: (state, data) =>
      (state.initializeTransfer = data),

    //update old transfer
    updateOldTransfer: (state, data) => (state.oldTransfer = data),

    //update transfer countdown
    updateCountDown: (state, data) => {
      if (parseInt(state.timer) === 0) return;
      state.timer -= data;
    },

    //update transfer timer
    updateTransferTimer: (state, time) => {
      state.timer = parseInt(time);
    },

    //update ledger transaction
    updateLedgerTransaction: (state, data) => (state.legderTransactions = data),

    //update ledger transaction details
    updateLedgeTransDetails: (state, data) => (state.ledgerTransDetails = data),

    //update gateway details
    updateGateway: (state, data) => (state.gateways = data),

    //update payout accounts
    updatePayoutAccount: (state, data) => (state.payaccount = data),
    updatePayCountry: (state, data) => (state.payCountry = data),

    //update payout
    updatePayout: (state, data) => (state.payout = data),

    //update payout transaction detail
    updatePayoutDetail: (state, data) => (state.payoutDetail = data),

    //update disputes
    updateDispute: (state, data) => (state.disputes = data),

    //update business account
    updateBankAccount: (state, data) => (state.bankAccounts = data),

    //update referrer
    updateReferrer: (state, data) => (state.referrer = data),

    //update two factor
    updateTwoFactor: (state, data) => (state.twoFactor = data),

    //update beneficiary list
    updateBeneficiaryList: (state, data) => (state.beneficiary = data),

    //update currency
    updateCurrency: (state, data) => (state.currency = data),

    //update scheduled payments
    updateSchedule: (state, data) => (state.schedule = data),

    //update employee
    updateEmployee: (state, data) => (state.employee = data),

    //updat schedule history
    updateScheduleHistory: (state, data) => (state.scheduleHistory = data),

    //update customer details
    updateCustomerDetails: (state, data) => (state.customerdetails = data),
    setSubAcctCusDetails: (state, data) => (state.customerdetails = data),

    //update current customer
    updateCurrentCustomer: (state, data) => (state.currentCustomer = data),

    //update bulkpayment breakdown details
    updatebulkpaymentBreakDown: (state, payload) =>
      (state.bulkpaymentBreakdown = payload),

    //update cac registrations
    updateCAC: (state, payload) => (state.cacreg = payload),

    //update account state
    updateAccountStatement: (state, payload) =>
      (state.account_statement = payload),

    //update business bank lists
    updateBusinessBanks: (state, data) => (state.businessBanks = data),

    //update cashier transactions
    updateCashierTransactions: (state, data) =>
      (state.cashiertransactions = data),

    updateBranchSalesChart: (state, data) => (state.branchSalesChart = data),

    updateBranchUsers: (state, data) => (state.branchUsers = data),

    updateBranchTransactions: (state, data) =>
      (state.branchTransactions = data),

    updateTeamBranchSales: (state, data) => (state.teamBranchSales = data),

    updateTvList: (state, data) => (state.tvlist = data),

    updateElectrictyProvider: (state, data) =>
      (state.electricityProvider = data),

    updateBranchStaff: (state, data) => (state.branchStaff = data),

    updateBranchDetails: (state, data) => (state.branchDetails = data),

    updatePayroll: (state, data) => (state.payroll = data),

    updatePayrollDetails: (state, data) => (state.payrollDetails = data),

    updateCashierBranchTrans: (state, data) =>
      (state.cashierBranchTrans = data),

    updateAlertSettings: (state, data) => (state.alertSettings = data),

    updateTeamList: (state, data) => (state.teamList = data),

    updatePendingTeamList: (state, data) => (state.pendingTeamList = data),

    updateProdCategories: (state, data) => (state.prodcategories = data),

    updateProducts: (state, data) => (state.products = data),

    updateStoreOrder: (state, data) => (state.storeOrders = data),
    updateAgents: (state, data) => (state.posAgents = data),
    updateTerminals: (state, data) => (state.posTerminals = data),
    updatePOSDash: (state, data) => (state.posDash = data),
    updatePOSTransactions: (state, data) => (state.posTransactions = data),
    updateBizFees: (state, data) => (state.bizFees = data),
    updateSuccessMsg: (state, data) => (state.successMsg = data),

    //update prices
    updateDefaultPrices: (state, data) => (state.defaultSubPrice = data),
    updatePremiumPrices: (state, data) => (state.premiumSubPrice = data),

    //cache
    updateCache: (state, details) =>
      (state.caches[details.key] = details.value),
  },
  actions: {
    //log user in
    loginUser: async (context, data) => {
      try {
        let formData = new FormData();
        formData.append("email", data.email);
        formData.append("pword", data.password);
        formData.append("env", "web");
        formData.append("platform", "bolddweb");
        formData.append("device_token", getOrCreateDeviceToken());

        return await fetch(
          `${BASE_URL}/loginzweb`,
          {
            method: "POST", // or 'PUT'
            body: formData,
          }
        );
      } catch (e) {
        return {
          data: {
            msg: e.message || e.msg || "Unable to connect to the sign-in service.",
            status: false,
          },
        };
      }
    },

    //log user in using 2fa
    login2Fa: async (context, data) => {
      try {
        let formData = new FormData();
        formData.append("userid", context.getters.getUser.userid);
        formData.append("secretcode", data.secretcode);
        formData.append("authtype", data.authtype);
        formData.append("env", "web");
        formData.append("platform", "bolddweb");
        formData.append("device_token", getOrCreateDeviceToken());
        if (data.trust_device !== undefined) {
          formData.append("trust_device", data.trust_device ? "1" : "0");
        }

        return await fetch(
          `${BASE_URL}/authenticatemeweb`,
          {
            method: "POST", // or 'PUT'
            body: formData,
          }
        );
      } catch (e) {
        return {
          data: {
            msg: e.msg,
            status: false,
          },
        };
      }
    },

    initAuthEmail: async (context) => {
      try {
        const formData = new FormData();
        formData.append("userid", context.getters.getUser.userid);
        formData.append("env", "web");

        const response = await fetch(`${BASE_URL}/authemail`, {
          method: "POST",
          body: formData,
        });

        return await response.json();
      } catch (e) {
        return {
          msg: e.message || "Unable to send email OTP. Please try again.",
          status: false,
        };
      }
    },

    switch2Fa: async (context, data) => {
      try {
        let formData = new FormData();
        formData.append("sesscode", context.getters.getUser.sesscode);
        formData.append("userid", context.getters.getUser.userid);
        formData.append("businessid", data.businessid);
        formData.append("logintype", data.logintype);
        formData.append("secretcode", data.secretcode);
        formData.append("env", "web");
        formData.append("platform", "bolddweb");
        formData.append("device_token", getOrCreateDeviceToken());
        if (data.trust_device !== undefined) {
          formData.append("trust_device", data.trust_device ? "1" : "0");
        }

        return await fetch(
          `${BASE_URL}/authenticatemeweb`,
          {
            method: "POST", // or 'PUT'
            body: formData,
          }
        );
      } catch (e) {
        return {
          data: {
            msg: e.msg,
            status: false,
          },
        };
      }
    },

    //fetch user details
    fetchUserCredentials: async (context) => {
      try {
        const formData = new FormData();
        formData.append("sesscode", context.getters.getUser.sesscode);
        formData.append("userid", context.getters.getUser.userid);
        formData.append("businessid", context.getters.getUser.business_id);
        formData.append("env", "web");

        await fetch(
          `${BASE_URL}/getinfo`,
          {
            method: "POST", // or 'PUT'
            body: formData,
          }
        )
          .then((response) => response.json())
          .then(async (response) => {
            if (response.status === false) {
              clearStoragePreserve(RESOLVED_DISPUTE_STORAGE_PREFIXES);
              bus.$emit("showDialog", {
                type: "error",
                title: "Session Expired!",
                message: "A new user session detected",
                action: "logout",
              });
              return;
            }
            if (response.status && response.account_category == "Not a fit") {
              clearStoragePreserve(RESOLVED_DISPUTE_STORAGE_PREFIXES);
              bus.$emit("showDialog", {
                type: "error",
                title: "Access Denied!",
                message:
                  "Your business has been classified as not a fit for Boldd. If you think this is a mistake, kindly reach out to our support. Thank you!",
                action: "logout",
              });
              return;
            }
            const currentDetails = context.getters.getUserDetails || {};
            return context.commit("updateUserDetails", {
              ...currentDetails,
              ...response,
              businesses:
                Array.isArray(response?.businesses) && response.businesses.length
                  ? response.businesses
                  : Array.isArray(currentDetails?.businesses)
                    ? currentDetails.businesses
                    : [],
            });
          });
      await context.dispatch("fetchBusinessLivenessProfile");
      } catch (e) {
        console.log(e.message);
      }
    },

    fetchBusinessLivenessProfile: async (context) => {
      try {
        const details = context.getters.getUserDetails || {};
        const secretKey = String(
          details?.livesecret ||
            details?.live_secret ||
            details?.testsecret ||
            details?.test_secret ||
            ''
        ).trim();
        const businessId = String(
          details?.business_id ||
            details?.businessid ||
            context.getters.getUser?.business_id ||
            ''
        ).trim();

        if (!secretKey || !businessId) return {};

        const response = await context.dispatch("httpRequest", {
          rawUrl:
            (process.env.VUE_APP_MAIN_API_BASE_URL || 'https://api.oneappgo.com/v1') +
            '/business/businessinfo.php',
          data: {
            secretkey: secretKey,
            business_id: businessId,
          },
        });

        if (response && response.status) {
          context.commit("updateUserDetails", {
            ...details,
            ...response,
            has_liveness_access: !!(response.has_liveness_access || response?.liveness?.enabled),
          });
        }

        return response;
      } catch (e) {
        console.log(e.message);
        return {};
      }
    },

    fetchVerificationStatus: async (context) => {
      try {
        const formData = new FormData();
        formData.append("sesscode", context.getters.getUser.sesscode);
        formData.append("userid", context.getters.getUser.userid);
        formData.append("businessid", context.getters.getUser.business_id);
        formData.append("env", "web");

        const res = await fetch(
          `${BASE_URL}/verificationcheck`,
          {
            method: "POST",
            body: formData,
          }
        );
        const response = await res.json();
        context.commit("updateVerificationStatus", response || {});
        context.commit("updateCache", {
          key: "verstatus",
          value: mapVerificationStatusToLegacyCache(response || {}),
        });
        if (context.getters.getUserDetails) {
          context.commit(
            "updateUserDetails",
            mergeVerificationIntoUserDetails(
              context.getters.getUserDetails,
              response || {}
            )
          );
        }
        return response;
      } catch (e) {
        console.log(e.message);
        context.commit("updateVerificationStatus", {});
        return {};
      }
    },

    //register new users
    registerUser: async (context, data) => {
      try {
        const formData = new FormData();
        formData.append("fname", data.firstname);
        formData.append("sname", data.lastname);
        formData.append("busname", data.businessName);
        formData.append("busemail", data.businessEmail);
        formData.append("countryCode", data.countryCode);
        formData.append("phn", data.phone);
        formData.append("pwd", data.password);
        formData.append("desc", data.businessDesc);
        formData.append("whorefer", data.whorefer);
        formData.append("referby", data.referby);
        formData.append("env", "web");

        return await fetch(
          `${BASE_URL}/createaccount  `,
          {
            method: "POST",
            body: formData,
          }
        );
      } catch (e) {
        console.log(e.message);
      }
    },

    //fetch user account details
    fetchAccountNos: async (context) => {
      await context.dispatch("httpRequest", {
        url: "accountno",
        caches: [{ key: "accountno", value: "banklist" }],
      });
    },

    //fetch bank details
    fetchBankDetails: async (context) => {
      try {
        await fetch(
          `${BASE_URL}/listbanks`
        )
          .then((response) => response.json())
          .then((response) => {
            context.commit("updateBankDetails", response);
          })
          .catch((e) => console.log(e.message));
      } catch (e) {
        console.log(e.message);
      }
    },

    //verify user bank details
    verifyBankDetails: async (context, data) => {
      try {
        const formData = new FormData();
        formData.append("sesscode", context.getters.getUser.sesscode);
        formData.append("userid", context.getters.getUser.userid);
        formData.append("businessid", context.getters.getUser.business_id);
        formData.append("acctno", data.acctno);
        formData.append("bankcode", data.bankcode);
        formData.append("bankname", data.bankname);
        formData.append("env", "web");

        return await fetch(
          `${BASE_URL}/verifybankacct `,
          {
            method: "POST",
            body: formData,
          }
        );
      } catch (e) {
        console.log(e.message);
      }
    },

    //submit bank verification
    submitVerification: async (context, data) => {
      try {
        const formData = new FormData();
        formData.append("sesscode", context.getters.getUser.sesscode);
        formData.append("userid", context.getters.getUser.userid);
        formData.append("businessid", context.getters.getUser.business_id);
        formData.append("acctno", data.acctno);
        formData.append("acctname", data.acctname);
        formData.append("bankcode", data.bankcode);
        formData.append("bankname", data.bankname);
        formData.append("bvnn", data.bvnn);
        formData.append("busadr", data.busadr);
        formData.append("env", "web");

        return await fetch(
          `${BASE_URL}/verifyme `,
          {
            method: "POST",
            body: formData,
          }
        );
      } catch (e) {
        console.log(e.message);
      }
    },

    //verify user bvn
    verifyBvn: async (context, data) => {
      try {
        const formData = new FormData();

        formData.append("acctno", data.acctno);
        formData.append("bankcode", data.bankcode);
        formData.append("bbv", data.bvn);
        formData.append("env", "web");

        return await fetch(
          `${BASE_URL}/bvncheck`,
          {
            method: "POST",
            body: formData,
          }
        );
      } catch (e) {
        console.log(e.message);
      }
    },

    //fetch user transactions
    fetchUserTransactions: async (context, data = {}) => {
      try {
        const formData = new FormData();

        formData.append("sesscode", context.getters.getUser.sesscode);
        formData.append("userid", context.getters.getUser.userid);
        formData.append("businessid", context.getters.getUser.business_id);
        formData.append("currency", context.getters.getCurrency || "NGN");
        formData.append("limit", data.limit || 2500);
        if (data.filterby !== undefined) {
          formData.append("filterby", data.filterby);
          formData.append("custom", data.custom ?? "");
        }
        formData.append("env", "web");

        await fetch(
          `${BASE_URL}/payments`,
          {
            method: "POST",
            body: formData,
          }
        )
          .then((response) => response.json())
          .then((response) => {
            context.commit("updateTransactions", response);
          })
          .catch((e) => console.log(e.message));
      } catch (e) {}
    },

    //fetch user invoice
    fetchUserInvoice: async (context) => {
      try {
        const formData = new FormData();

        formData.append("sesscode", context.getters.getUser.sesscode);
        formData.append("userid", context.getters.getUser.userid);
        formData.append("businessid", context.getters.getUser.business_id);
        formData.append("env", "web");

        await fetch(
          `${BASE_URL}/invoices`,
          {
            method: "POST",
            body: formData,
          }
        )
          .then((response) => response.json())
          .then((response) => {
            context.commit("updateInvoice", response);
          })
          .catch((e) => console.log(e.message));
      } catch (e) {}
    },

    //fetch user customers
    fetchUserCustomers: async (context) => {
      try {
        const formData = new FormData();

        formData.append("sesscode", context.getters.getUser.sesscode);
        formData.append("userid", context.getters.getUser.userid);
        formData.append("businessid", context.getters.getUser.business_id);
        formData.append("env", "web");

        await fetch(
          `${BASE_URL}/customers`,
          {
            method: "POST",
            body: formData,
          }
        )
          .then((response) => response.json())
          .then((response) => {
            context.commit("updateCustomers", response);
          })
          .catch((e) => console.log(e.message));
      } catch (e) {}
    },

    //fetch state
    fetchStates: async (context) => {
      try {
        await fetch(
          `${BASE_URL}/statelists`,
          {
            method: "GET",
          }
        )
          .then((response) => response.json())
          .then((response) => {
            context.commit("updateState", response);
          })
          .catch((e) => console.log(e.message));
      } catch (e) {}
    },

    //fetch country
    fetchCountry: async (context) => {
      try {
        await fetch(
          `${BASE_URL}/countries-lists`,
          {
            method: "GET",
          }
        )
          .then((response) => response.json())
          .then((response) => {
            context.commit("updateCountry", response);
          })
          .catch((e) => console.log(e.message));
      } catch (e) {}
    },

    //fetch town
    fetchCity: async (context, data) => {
      try {
        const form = new FormData();
        form.append("state", data.state);
        form.append("env", "web");
        await fetch(
          `${BASE_URL}/citylists`,
          {
            method: "POST",
            body: form,
          }
        )
          .then((response) => response.json())
          .then((response) => {
            context.commit("updateCity", response);
          })
          .catch((e) => console.log(e.message));
      } catch (e) {}
    },

    //submit user transfer request
    submitTransfer: async (context, data) => {
      try {
        const formData = new FormData();
        formData.append("sesscode", context.getters.getUser.sesscode);
        formData.append("userid", context.getters.getUser.userid);
        formData.append("businessid", context.getters.getUser.business_id);
        formData.append("acctno", data.acctno);
        formData.append("acctname", data.acctname);
        formData.append("bankcode", data.bankcode);
        formData.append("bankname", data.bankname);
        formData.append("amount", data.amount);
        formData.append("desc", data.desc);
        formData.append("transpin", data.transpin || data.pin || "");
        formData.append("ctoken", data.ctoken);
        formData.append("trackingid", data.trackingid ?? "");
        formData.append("subaccount", data.subaccount ?? "");
        formData.append("env", "web");

        return await fetch(
          `${BASE_URL}/sendmoney `,
          {
            method: "POST",
            body: formData,
          }
        );
      } catch (e) {
        console.log(e.message);
      }
    },

    //reset user password
    resetPassword: async (context, data) => {
      try {
        const form = new FormData();
        form.append("hisemail", data.email);
        form.append("env", "web");
        return await fetch(
          `${BASE_URL}/reset-password`,
          {
            method: "POST",
            body: form,
          }
        );
      } catch (e) {
        console.log(e.message);
      }
    },

    //update user password
    updatePassword: async (context, data) => {
      try {
        const form = new FormData();
        form.append("accountid", data.accountid || "");
        form.append("token", data.token || "");
        form.append("otpcode", data.otp);
        form.append("twofacode", data.twofacode || "");
        form.append("newpass", data.password);
        form.append("confirmpass", data.confirmpass);
        form.append("env", "web");

        return await fetch(
          `${BASE_URL}/recover-password`,
          {
            method: "POST",
            body: form,
          }
        );
      } catch (e) {
        console.log(e.message);
      }
    },

    //update user business profile
    updateUserBusiness: async (context, data) => {
      try {
        const formData = new FormData();
        formData.append("sesscode", context.getters.getUser.sesscode);
        formData.append("userid", context.getters.getUser.userid);
        formData.append("businessid", context.getters.getUser.business_id);
        formData.append("businessname", data.businessname);
        formData.append("business_adr", data.business_adr);
        formData.append("business_phone", data.business_phone);
        formData.append("business_email", data.business_email);
        formData.append("state", data.state);
        formData.append("city", data.city);
        formData.append("lga", data.lga);
        formData.append("tradename", data.tradename);
        formData.append("othertown", data.othertown);
        formData.append("business_desc", data.business_desc);
        formData.append("env", "web");

        return await fetch(
          `${BASE_URL}/business-settings`,
          {
            method: "POST",
            body: formData,
          }
        );
      } catch (e) {
        console.log(e.message);
      }
    },

    //fetch user payments
    fetchUserPayments: async (context) => {
      try {
        const formData = new FormData();
        formData.append("sesscode", context.getters.getUser.sesscode);
        formData.append("userid", context.getters.getUser.userid);
        formData.append("businessid", context.getters.getUser.business_id);
        formData.append("env", "web");

        await fetch(
          `${BASE_URL}/payments`,
          {
            method: "POST",
            body: formData,
          }
        )
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              context.commit("updatePayments", response);
            }
          })
          .catch((e) => console.log(e.message));
      } catch (e) {
        console.log(e.message);
      }
    },

    //fetch data plan
    fetchDataPlans: async (context, data) => {
      try {
        const formData = new FormData();
        formData.append("sesscode", context.getters.getUser.sesscode);
        formData.append("userid", context.getters.getUser.userid);
        formData.append("businessid", context.getters.getUser.business_id);
        formData.append("provider", data.provider);
        formData.append("env", "web");

        await fetch(
          `${BASE_URL}/getdataplans`,
          {
            method: "POST",
            body: formData,
          }
        )
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              context.commit("updateDataPlans", response);
            }
          })
          .catch((e) => console.log(e.message));
      } catch (e) {
        console.log(e.message);
      }
    },

    //verify email
    verifyEmail: async (context) => {
      try {
        const formData = new FormData();
        formData.append("sesscode", context.getters.getUser.sesscode);
        formData.append("userid", context.getters.getUser.userid);
        formData.append("businessid", context.getters.getUser.business_id);
        formData.append("env", "web");

        return await fetch(
          `${BASE_URL}/verifyemail`,
          {
            method: "POST",
            body: formData,
          }
        );
      } catch (e) {
        console.log(e.message);
      }
    },

    //upload document
    uploadDocument: async (context, data) => {
      try {
        const uploadFile = await prepareVerificationUploadFile(data.file);
        const formData = new FormData();
        formData.append("sesscode", context.getters.getUser.sesscode);
        formData.append("userid", context.getters.getUser.userid);
        formData.append("businessid", context.getters.getUser.business_id);
        formData.append(data.mode, uploadFile, uploadFile.name);
        formData.append("env", "web");

        return await fetch(
          `${BASE_URL}/verify-docs`,
          {
            method: "POST",
            body: formData,
          }
        );
      } catch (e) {
        console.log(e.message);
        return buildFetchFallbackResponse(
          e.message || "Unable to upload document.",
          400
        );
      }
    },

    //upload office building documents
    uploadOfficeDocuments: async (context, data) => {
      try {
        const uploadFile = await prepareVerificationUploadFile(data.file);
        const formData = new FormData();
        formData.append("sesscode", context.getters.getUser.sesscode);
        formData.append("userid", context.getters.getUser.userid);
        formData.append("businessid", context.getters.getUser.business_id);
        formData.append("file", uploadFile, uploadFile.name);
        formData.append("env", "web");

        return await fetch(
          `${BASE_URL}/otherverdocs`,
          {
            method: "POST",
            body: formData,
          }
        );
      } catch (e) {
        console.log(e.message);
        return buildFetchFallbackResponse(
          e.message || "Unable to upload document.",
          400
        );
      }
    },

    //fetch transaction detail
    fetchTransactionDetail: async (context, data) => {
      try {
        const formData = new FormData();
        formData.append("sesscode", context.getters.getUser.sesscode);
        formData.append("userid", context.getters.getUser.userid);
        formData.append("businessid", context.getters.getUser.business_id);
        formData.append("reference", data.reference);
        formData.append("mode", data.mode);
        formData.append("env", "web");

        await fetch(
          `${BASE_URL}/paydetails `,
          {
            method: "POST",
            body: formData,
          }
        )
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              context.commit("updateTransactionDetail", response);
            }
          })
          .catch((e) => console.log(e.message));
      } catch (e) {
        console.log(e.message);
      }
    },

    //purchase data
    purchaseData: async (context, data) => {
      try {
        const formData = new FormData();
        formData.append("sesscode", context.getters.getUser.sesscode);
        formData.append("userid", context.getters.getUser.userid);
        formData.append("businessid", context.getters.getUser.business_id);
        formData.append("phoneno", data.phoneno);
        formData.append("datacode", data.datacode);
        formData.append("network", data.network);
        formData.append("transpin", data.transpin || data.pin || "");
        formData.append("ctoken", data.ctoken);
        formData.append("env", "web");

        return await fetch(
          `${BASE_URL}/databundle `,
          {
            method: "POST",
            body: formData,
          }
        );
      } catch (e) {
        console.log(e.message);
      }
    },

    //fetch airtime prices
    fetchAirtimePrice: async (context, data) => {
      try {
        const formData = new FormData();
        formData.append("sesscode", context.getters.getUser.sesscode);
        formData.append("userid", context.getters.getUser.userid);
        formData.append("businessid", context.getters.getUser.business_id);
        formData.append("network", data.network);
        formData.append("amount", data.amount);
        formData.append("env", "web");

        await fetch(
          `${BASE_URL}/getprices`,
          {
            method: "POST",
            body: formData,
          }
        )
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              context.commit("updateAirtimePrice", response);
            }
          })
          .catch((e) => console.log(e.message));
      } catch (e) {
        console.log(e.message);
      }
    },

    //purchase airtime
    purchaseAirtime: async (context, data) => {
      try {
        const formData = new FormData();
        formData.append("sesscode", context.getters.getUser.sesscode);
        formData.append("userid", context.getters.getUser.userid);
        formData.append("businessid", context.getters.getUser.business_id);
        formData.append("phoneno", data.phoneno);
        formData.append("ntwk", data.ntwk);
        formData.append("amount", data.amount);
        formData.append("transpin", data.transpin || data.pin || "");
        formData.append("ctoken", data.ctoken);
        formData.append("env", "web");

        return await fetch(
          `${BASE_URL}/airtime `,
          {
            method: "POST",
            body: formData,
          }
        );
      } catch (e) {
        console.log(e.message);
      }
    },

    //fetch cable tv prices
    fetchCableTvPrice: async (context, data) => {
      try {
        const formData = new FormData();
        formData.append("sesscode", context.getters.getUser.sesscode);
        formData.append("userid", context.getters.getUser.userid);
        formData.append("businessid", context.getters.getUser.business_id);
        formData.append("tv", data.tv);
        formData.append("env", "web");

        await fetch(
          `${BASE_URL}/gettvplan`,
          {
            method: "POST",
            body: formData,
          }
        )
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              context.commit("updateCableTV", response);
            }
          })
          .catch((e) => console.log(e.message));
      } catch (e) {
        console.log(e.message);
      }
    },

    // create transaction pin
    createTransactionPin: async (context, data) => {
      try {
        const formData = new FormData();
        formData.append("sesscode", context.getters.getUser.sesscode);
        formData.append("userid", context.getters.getUser.userid);
        formData.append("businessid", context.getters.getUser.business_id);
        formData.append("pin", data.pin);
        formData.append("retrypin", data.retrypin);
        formData.append("env", "web");

        return await fetch(
          `${BASE_URL}/transpin`,
          {
            method: "POST",
            body: formData,
          }
        );
      } catch (e) {
        console.log(e.message);
      }
    },

    // create auth transaction pin
    createAuthTransPin: async (context, data) => {
      try {
        const formData = new FormData();
        formData.append("userid", context.getters.getUser.userid);
        formData.append("newpin", data.pin);
        formData.append("confirmpin", data.confirmpin);
        formData.append("env", "web");

        return await fetch(
          `${BASE_URL}/authpinsetup`,
          {
            method: "POST",
            body: formData,
          }
        );
      } catch (e) {
        console.log(e.message);
      }
    },

    //update user transaction pin
    updateTransactionPin: async (context, data) => {
      try {
        const formData = new FormData();
        formData.append("sesscode", context.getters.getUser.sesscode);
        formData.append("userid", context.getters.getUser.userid);
        formData.append("businessid", context.getters.getUser.business_id);
        formData.append("oldpin", data.oldpin);
        formData.append("newpin", data.newpin);
        formData.append("confirmpin", data.confirmpin);
        formData.append("env", "web");

        return await fetch(
          `${BASE_URL}/updatepin`,
          {
            method: "POST",
            body: formData,
          }
        );
      } catch (e) {
        console.log(e.message);
      }
    },

    //upload user image
    uploadProfile: async (context, data) => {
      try {
        const formData = new FormData();
        formData.append("sesscode", context.getters.getUser.sesscode);
        formData.append("userid", context.getters.getUser.userid);
        formData.append("businessid", context.getters.getUser.business_id);
        formData.append("image", data.image);
        formData.append("env", "web");

        return await fetch(
          `${BASE_URL}/uploadpic`,
          {
            method: "POST",
            body: formData,
          }
        );
      } catch (e) {
        console.log(e.message);
      }
    },

    //change user password
    changePassword: async (context, data) => {
      try {
        const formData = new FormData();
        formData.append("sesscode", context.getters.getUser.sesscode);
        formData.append("userid", context.getters.getUser.userid);
        formData.append("businessid", context.getters.getUser.business_id);
        formData.append("currentpass", data.currentpass);
        formData.append("newpass", data.newpass);
        formData.append("retypepass", data.retypepass);
        formData.append("env", "web");

        return await fetch(
          `${BASE_URL}/change-pass`,
          {
            method: "POST",
            body: formData,
          }
        );
      } catch (e) {
        console.log(e.message);
      }
    },

    //fetch team members
    fetchTeamMembers: async (context) => {
      try {
        const formData = new FormData();
        formData.append("sesscode", context.getters.getUser.sesscode);
        formData.append("userid", context.getters.getUser.userid);
        formData.append("businessid", context.getters.getUser.business_id);
        formData.append("env", "web");

        await fetch(
          `${BASE_URL}/team-members `,
          {
            method: "POST",
            body: formData,
          }
        )
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              context.commit("updateTeamMembers", response);
            }
          })
          .catch((e) => console.log(e.message));
      } catch (e) {
        console.log(e.message);
      }
    },

    //send Team Invite
    sendTeamInvite: async (context, data) => {
      try {
        const formData = new FormData();
        formData.append("sesscode", context.getters.getUser.sesscode);
        formData.append("userid", context.getters.getUser.userid);
        formData.append("businessid", context.getters.getUser.business_id);
        formData.append("useremail", data.useremail);
        formData.append("is_dev", data.is_dev);
        formData.append("role", data.role);
        formData.append("branchid", data.branchid);
        formData.append("expired", data.expired);
        formData.append("name", data.name);
        formData.append("phoneno", data.phoneno);
        formData.append("env", "web");

        return await fetch(
          `${BASE_URL}/inviteteam`,
          {
            method: "POST",
            body: formData,
          }
        );
      } catch (e) {
        console.log(e.message);
      }
    },

    //fetch web hook
    fetchWebHook: async (context) => {
      try {
        const formData = new FormData();
        formData.append("sesscode", context.getters.getUser.sesscode);
        formData.append("userid", context.getters.getUser.userid);
        formData.append("businessid", context.getters.getUser.business_id);
        formData.append("env", "web");

        await fetch(
          `${BASE_URL}/devtools`,
          {
            method: "POST",
            body: formData,
          }
        )
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              context.commit("updateWebHook", response);
            }
          })
          .catch((e) => console.log(e.message));
      } catch (e) {
        console.log(e.message);
      }
    },

    //save webhook
    saveWebHook: async (context, data) => {
      try {
        const formData = new FormData();
        formData.append("sesscode", context.getters.getUser.sesscode);
        formData.append("userid", context.getters.getUser.userid);
        formData.append("businessid", context.getters.getUser.business_id);
        formData.append("hookurl", data.hookurl);
        if (data.iplisted !== undefined) {
          formData.append("iplisted", data.iplisted);
        }
        if (data.ipreason !== undefined) {
          formData.append("ipreason", data.ipreason);
        }
        formData.append("env", "web");

        return await fetch(
          `${BASE_URL}/savehookurl`,
          {
            method: "POST",
            body: formData,
          }
        );
      } catch (e) {
        console.log(e.message);
      }
    },

    //generate webhook api
    generateWebHook: async (context) => {
      try {
        const formData = new FormData();
        formData.append("sesscode", context.getters.getUser.sesscode);
        formData.append("userid", context.getters.getUser.userid);
        formData.append("businessid", context.getters.getUser.business_id);
        formData.append("env", "web");

        return await fetch(
          `${BASE_URL}/generatekeys`,
          {
            method: "POST",
            body: formData,
          }
        );
      } catch (e) {
        console.log(e.message);
      }
    },

    //fetch inflow or outflow
    fetchFlow: async (context, mode) => {
      try {
        const formData = new FormData();
        formData.append("sesscode", context.getters.getUser.sesscode);
        formData.append("userid", context.getters.getUser.userid);
        formData.append("businessid", context.getters.getUser.business_id);
        formData.append("type", mode);
        formData.append("env", "web");

        await fetch(
          `${BASE_URL}/recent-trans`,
          {
            method: "POST",
            body: formData,
          }
        )
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              if (mode === "credit")
                return context.commit("updateInflow", response);
              if (mode === "debit")
                return context.commit("updateOutflow", response);
              return;
            }

            if (mode === "credit") return context.commit("updateInflow", "");
            if (mode === "debit") return context.commit("updateOutflow", "");
          })
          .catch((e) => console.log(e.message));
      } catch (e) {
        console.log(e.message);
      }
    },

    //fetch custom cities
    fetchCustomCity: async (context, data) => {
      try {
        const form = new FormData();
        form.append("state", data.state);
        form.append("env", "web");

        return await fetch(
          `${BASE_URL}/citylists`,
          {
            method: "POST",
            body: form,
          }
        );
      } catch (e) {
        console.log(e.message);
      }
    },

    //submit cac payment
    submitCacPayment: async (context, data) => {
      try {
        const formData = new FormData();
        formData.append("sesscode", context.getters.getUser.sesscode);
        formData.append("userid", context.getters.getUser.userid);
        formData.append("businessid", context.getters.getUser.business_id);
        formData.append("regmode", data.regmode);
        formData.append("env", "web");

        return await fetch(
          `${BASE_URL}/payforcac`,
          {
            method: "POST",
            body: formData,
          }
        );
      } catch (e) {
        console.log(e.message);
      }
    },

    //submit cac information
    submitCAC: async (context, data) => {
      try {
        const form = new FormData();
        form.append("sesscode", context.getters.getUser.sesscode);
        form.append("userid", context.getters.getUser.userid);
        form.append("businessid", context.getters.getUser.business_id);
        form.append("regmode", data.regmode);
        form.append("proposed_name1", data.proposed_name1);
        form.append("proposed_name2", data.proposed_name2);
        form.append("proposed_name3", data.proposed_name3);
        form.append("bizcommence_date", data.bizcommence_date);
        form.append("business_nature", data.business_nature);
        form.append("business_adr", data.business_adr);
        form.append("business_state", data.business_state);
        form.append("business_city", data.business_city);
        form.append("business_adr2", data.business_adr2);
        form.append("business_state2", data.business_state2);
        form.append("business_city2", data.business_city2);
        form.append("owner_sname", data.owner_sname);
        form.append("owner_fname", data.owner_fname);
        form.append("owner_oname", data.owner_oname);
        form.append("owner_nationality", data.owner_nationality);
        form.append("former_nationality", data.former_nationality);
        form.append("owner_gender", data.owner_gender);
        form.append("owner_dob", data.owner_dob);
        form.append("owner_phone", data.owner_phone);
        form.append("owner_email", data.owner_email);
        form.append("idtype", data.idtype);
        form.append("idcardno", data.idcardno);
        form.append("occupation", data.occupation);
        form.append("owner_adr", data.owner_adr);
        form.append("owner_state", data.owner_state);
        form.append("owner_city", data.owner_city);
        form.append("paymentref", data.paymentref);
        form.append("idcard", data.idcard);
        form.append("owner_passport", data.owner_passport);
        form.append("owner_signature", data.owner_signature);
        form.append("env", "web");

        return await fetch(
          `${BASE_URL}/regcac`,
          {
            method: "POST",
            body: form,
          }
        );
      } catch (e) {
        console.log(e.message);
      }
    },

    //payment link
    fetchPaymentLink: async (context) => {
      try {
        const formData = new FormData();
        formData.append("sesscode", context.getters.getUser.sesscode);
        formData.append("userid", context.getters.getUser.userid);
        formData.append("businessid", context.getters.getUser.business_id);
        formData.append("env", "web");

        await fetch(
          `${BASE_URL}/paylinks`,
          {
            method: "POST",
            body: formData,
          }
        )
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              context.commit("updatePaymentLink", response);
            }
          })
          .catch((e) => console.log(e.message));
      } catch (e) {
        console.log(e.message);
      }
    },

    //create payment link
    createPaymentLink: async (context, data) => {
      try {
        const formData = new FormData();
        formData.append("sesscode", context.getters.getUser.sesscode);
        formData.append("userid", context.getters.getUser.userid);
        formData.append("businessid", context.getters.getUser.business_id);
        formData.append("pagename", data.pagename);
        formData.append("currency", data.currency);
        formData.append("url", data.url);
        formData.append("amnt", data.amnt);
        formData.append("desc", data.desc);
        formData.append("env", "web");

        return await fetch(
          `${BASE_URL}/single-paylink`,
          {
            method: "POST",
            body: formData,
          }
        );
      } catch (e) {
        console.log(e.message);
      }
    },

    //disable payment link
    togglePaymentLink: async (context, data) => {
      try {
        const formData = new FormData();
        formData.append("sesscode", context.getters.getUser.sesscode);
        formData.append("userid", context.getters.getUser.userid);
        formData.append("businessid", context.getters.getUser.business_id);
        formData.append("pageid", data.pageid);
        formData.append("mode", data.mode);
        formData.append("env", "web");

        return await fetch(
          `${BASE_URL}/update-singlepay`,
          {
            method: "POST",
            body: formData,
          }
        );
      } catch (e) {
        console.log(e.message);
      }
    },

    //fetch payment link transaction
    fetchPaymentLinkTransaction: async (context, data) => {
      try {
        const formData = new FormData();
        formData.append("sesscode", context.getters.getUser.sesscode);
        formData.append("userid", context.getters.getUser.userid);
        formData.append("businessid", context.getters.getUser.business_id);
        formData.append("pageid", data.pageid);
        formData.append("pagetype", data.pagetype);
        formData.append("env", "web");

        await fetch(
          `${BASE_URL}/pagetrans`,
          {
            method: "POST",
            body: formData,
          }
        )
          .then((response) => response.json())
          .then((response) => {
            return context.commit("updatePaymentLinkTransaction", response);
          })
          .catch((e) => console.log(e.message));
      } catch (e) {
        console.log(e.message);
      }
    },

    //fetch business info
    fetchBusinessInfo: async (context, paylink) => {
      try {
        let formData = new FormData();
        formData.append("plink", paylink);
        formData.append("env", "web");
        await fetch(
          `${BASE_URL}/thebusinfo`,
          {
            method: "POST",
            body: formData,
          }
        )
          .then((response) => response.json())
          .then((response) => {
            return context.commit("updateBusinessInfo", response);
          })
          .catch((e) => console.log(e.message));
      } catch (e) {
        console.log(e.message);
      }
    },

    //initiate payment
    initiatePayment: async (context, data) => {
      try {
        let formData = new FormData();
        formData.append("amount", data.amount);
        formData.append("fname", data.fname);
        formData.append("lname", data.lname);
        formData.append("phn", data.phn);
        formData.append("currency", data.currency);
        formData.append("email", data.email);
        formData.append("pagetype", data.pagetype);
        formData.append("businessid", data.bisid);
        formData.append("pageid", data.pageid);
        formData.append("env", "web");

        return await fetch(
          `${BASE_URL}/initiatepayment`,
          {
            method: "POST",
            body: formData,
          }
        );
      } catch (e) {
        console.log(e.message);
      }
    },

    //fetch check out details
    fetchCheckOutDetails: async (context, data) => {
      try {
        let formData = new FormData();
        formData.append("token", data.token);
        formData.append("reference", data.reference);
        formData.append("env", "web");

        await fetch(
          `${BASE_URL}/loadthecheckout`,
          {
            method: "POST",
            body: formData,
          }
        )
          .then((response) => response.json())
          .then((response) => {
            return context.commit("updateCheckOutDetails", response);
          })
          .catch((e) => console.log(e.message));
      } catch (e) {
        console.log(e.message);
      }
    },

    //process transactions
    processTransaction: async (context, data) => {
      try {
        let formData = new FormData();
        formData.append("token", data.token);
        formData.append("reference", data.reference);
        formData.append("gateway", data.gateway);
        formData.append("redirectlink", data.redirectlink);
        formData.append("env", "web");

        return await fetch(
          `${BASE_URL}/process-payment`,
          {
            method: "POST",
            body: formData,
          }
        );
      } catch (e) {
        console.log(e.message);
      }
    },

    //verify transactions
    verifyTransaction: async (context, data) => {
      try {
        let formData = new FormData();

        formData.append("reference", data.reference);
        formData.append("confirm", data.confirm);
        formData.append("gateway", data.gateway);
        formData.append("gatewayref", data.gatewayref);
        formData.append("gatewaystatus", data.gatewaystatus);
        formData.append("env", "web");

        await fetch(
          `${BASE_URL}/verify-transaction`,
          {
            method: "POST",
            body: formData,
          }
        )
          .then((response) => response.json())
          .then((response) => {
            return context.commit("updateVerifiedTransaction", response);
          })
          .catch((e) => console.log(e.message));
      } catch (e) {
        console.log(e.message);
      }
    },

    //verify user account
    verifyUserLogin: async (context, data) => {
      try {
        let formData = new FormData();
        formData.append("ref", data.ref);
        formData.append("token", data.token);
        formData.append("email", data.email);
        formData.append("pwd", data.pwd);
        formData.append("env", "web");

        return await fetch(
          `${BASE_URL}/verifypaymentlogin`,
          {
            method: "POST",
            body: formData,
          }
        );
      } catch (e) {
        console.log(e.message);
      }
    },

    //confirm login otp
    verifyOTP: async (context, data) => {
      try {
        let formData = new FormData();
        formData.append("ref", data.ref);
        formData.append("token", data.token);
        formData.append("otp", data.otp);
        formData.append("appuserid", data.appuserid);
        formData.append("env", "web");

        return await fetch(
          `${BASE_URL}/chckloginotp`,
          {
            method: "POST",
            body: formData,
          }
        );
      } catch (e) {
        console.log(e.message);
      }
    },

    //fetch audit log
    fetchAuditLog: async (context) => {
      try {
        let formData = new FormData();

        formData.append("sesscode", context.getters.getUser.sesscode);
        formData.append("userid", context.getters.getUser.userid);
        formData.append("businessid", context.getters.getUser.business_id);
        formData.append("env", "web");

        await fetch(
          `${BASE_URL}/auditlog`,
          {
            method: "POST",
            body: formData,
          }
        )
          .then((response) => response.json())
          .then((response) => {
            return context.commit("updateAuditLog", response);
          })
          .catch((e) => console.log(e.message));
      } catch (e) {
        console.log(e.message);
      }
    },

    //fetch audit log detail
    fetchAuditLogDetail: async (context, data) => {
      try {
        let formData = new FormData();

        formData.append("sesscode", context.getters.getUser.sesscode);
        formData.append("userid", context.getters.getUser.userid);
        formData.append("businessid", context.getters.getUser.business_id);
        formData.append("audit_id", data.audit_id);
        formData.append("env", "web");

        await fetch(
          `${BASE_URL}/auditdetails`,
          {
            method: "POST",
            body: formData,
          }
        )
          .then((response) => response.json())
          .then((response) => {
            return context.commit("updateAuditLogDetail", response);
          })
          .catch((e) => console.log(e.message));
      } catch (e) {
        console.log(e.message);
      }
    },

    //initialize transfer
    intializeTransfer: async (context, data) => {
      try {
        let formData = new FormData();
        formData.append("reference", data.reference);
        formData.append("token", data.token);
        formData.append("gateway", data.gateway);
        formData.append("env", "web");

        return await fetch(
          `${BASE_URL}/oneappmethod`,
          {
            method: "POST",
            body: formData,
          }
        )
          .then((response) => response.json())
          .then((response) => {
            context.commit("updateInitializeTransfer", response);
            if (!context.getters.getTransferTime)
              return context.commit("updateTransferTimer", response.countdown);
          })
          .catch((e) => console.log(e.message));
      } catch (e) {
        console.log(e.message);
      }
    },

    //transfer time out countdown
    transferTimeCountDown: async (context) => {
      setInterval(async () => {
        if (context.getters.getTransferTime <= 0) {
          clearInterval();

          return;
        }
        context.commit("updateCountDown", 1);
      }, 1005);
    },

    //check dynamic transfer
    checkDynamicTransfer: async (context, data) => {
      try {
        let formData = new FormData();
        formData.append("reference", data.reference);
        formData.append("token", data.token);
        formData.append("accntno", data.accntno);
        formData.append("amount", data.amount);
        formData.append("env", "web");

        return await fetch(
          `${BASE_URL}/checkdynamic-transfer`,
          {
            method: "POST",
            body: formData,
          }
        );
      } catch (e) {
        console.log(e.message);
      }
    },

    //toggle user environment
    changeEnvironment: async (context, data) => {
      try {
        let formData = new FormData();
        formData.append("sesscode", context.getters.getUser.sesscode);
        formData.append("userid", context.getters.getUser.userid);
        formData.append("businessid", context.getters.getUser.business_id);
        formData.append("mode", data.mode);
        formData.append("env", "web");

        return await fetch(
          `${BASE_URL}/switch-env`,
          {
            method: "POST",
            body: formData,
          }
        );
      } catch (e) {
        console.log(e.message);
      }
    },

    //fetch all ledger transactions
    fetchLedgerTransaction: async (context, data = {}) => {
      try {
        let formData = new FormData();
        formData.append("sesscode", context.getters.getUser.sesscode);
        formData.append("userid", context.getters.getUser.userid);
        formData.append("businessid", context.getters.getUser.business_id);
        formData.append("currency", context.getters.getCurrency);
        if (data.filterby !== undefined) {
          formData.append("filterby", data.filterby);
          formData.append("custom", data.custom ?? "");
        }
        formData.append("env", "web");

        return await fetch(
          `${BASE_URL}/ledger-transactions`,
          {
            method: "POST",
            body: formData,
          }
        )
          .then((response) => response.json())
          .then((response) => {
            context.commit("updateLedgerTransaction", response);
          })
          .catch((e) => console.log(e.message));
      } catch (e) {
        console.log(e.message);
      }
    },

    //fetch ledger transaction detail
    fetchLedgerTransDetails: async (context, data) => {
      try {
        let formData = new FormData();
        formData.append("sesscode", context.getters.getUser.sesscode);
        formData.append("userid", context.getters.getUser.userid);
        formData.append("businessid", context.getters.getUser.business_id);
        formData.append("reference", data.reference);
        formData.append("env", "web");

        return await fetch(
          `${BASE_URL}/ledger-transdetails`,
          {
            method: "POST",
            body: formData,
          }
        )
          .then((response) => response.json())
          .then((response) => {
            context.commit("updateLedgeTransDetails", response);
          })
          .catch((e) => console.log(e.message));
      } catch (e) {
        console.log(e.message);
      }
    },

    //fetch gateways
    fetchGateways: async (context) => {
      try {
        let formData = new FormData();
        formData.append("sesscode", context.getters.getUser.sesscode);
        formData.append("userid", context.getters.getUser.userid);
        formData.append("businessid", context.getters.getUser.business_id);
        formData.append("env", "web");

        return await fetch(
          `${BASE_URL}/checkgateways`,
          {
            method: "POST",
            body: formData,
          }
        )
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              context.commit("updateGateway", response);
            }
          })
          .catch((e) => console.log(e.message));
      } catch (e) {
        console.log(e.message);
      }
    },

    //update user payment gateway settings
    submitGatewaySettings: async (context, data) => {
      try {
        let formData = new FormData();
        formData.append("sesscode", context.getters.getUser.sesscode);
        formData.append("userid", context.getters.getUser.userid);
        formData.append("businessid", context.getters.getUser.business_id);
        formData.append("paywith_oneapp", data.paywith_oneapp);
        formData.append("paywith_transfer", data.paywith_transfer);
        formData.append("paywith_card", data.paywith_card);
        formData.append("env", "web");

        return await fetch(
          `${BASE_URL}/gateway-settings`,
          {
            method: "POST",
            body: formData,
          }
        );
      } catch (e) {
        console.log(e.message);
      }
    },

    //charge a saved Boldd card token
    chargeSavedCard: async (context, data) => {
      try {
        const formData = new FormData();
        formData.append("sesscode", context.getters.getUser.sesscode);
        formData.append("userid", context.getters.getUser.userid);
        formData.append("businessid", context.getters.getUser.business_id);
        formData.append("cardid", data.cardid);
        formData.append("amount", data.amount);
        formData.append("reference", data.reference || "");
        formData.append("currency", data.currency || "NGN");
        formData.append("description", data.description || "Recurring card charge");
        formData.append(
          "redirecturl",
          data.redirecturl || sessionStorage.getItem("payment_return_url") || ""
        );
        formData.append("env", "web");

        return await fetch(
          `${BASE_URL}/charge-saved-card`,
          {
            method: "POST",
            body: formData,
          }
        );
      } catch (e) {
        console.log(e.message);
      }
    },

    //update user transaction fee charges
    updateTransactionChargee: async (context, data) => {
      try {
        let formData = new FormData();
        formData.append("sesscode", context.getters.getUser.sesscode);
        formData.append("userid", context.getters.getUser.userid);
        formData.append("businessid", context.getters.getUser.business_id);
        formData.append("whopay", data.whopay);
        formData.append("env", "web");

        return await fetch(
          `${BASE_URL}/transfee-settings`,
          {
            method: "POST",
            body: formData,
          }
        );
      } catch (e) {
        console.log(e.message);
      }
    },

    //toggle user 2fa
    toggle2fa: async (context, data) => {
      try {
        let formData = new FormData();
        formData.append("sesscode", context.getters.getUser.sesscode);
        formData.append("userid", context.getters.getUser.userid);
        formData.append("businessid", context.getters.getUser.business_id);
        formData.append("login2fa", data.login2fa);
        formData.append("transfer2fa", data.transfer2fa);
        formData.append("env", "web");

        return await fetch(
          `${BASE_URL}/twofactor`,
          {
            method: "POST",
            body: formData,
          }
        );
      } catch (e) {
        console.log(e.message);
      }
    },

    //verify user login details
    verifyLoginDetails: async (context, data) => {
      try {
        let formData = new FormData();
        formData.append("emm", data.emm);
        formData.append("pww", data.pww);
        formData.append("env", "web");

        return await fetch(
          `${BASE_URL}/validate-accountcheck`,
          {
            method: "POST",
            body: formData,
          }
        );
      } catch (e) {
        console.log(e.message);
      }
    },

    //generate user upgrade otp
    generateOTP: async (context, data) => {
      try {
        let formData = new FormData();
        formData.append("useremail", data.useremail);
        formData.append("env", "web");

        return await fetch(
          `${BASE_URL}/upgradeotp`,
          {
            method: "POST",
            body: formData,
          }
        );
      } catch (e) {
        console.log(e.message);
      }
    },

    //verify user upgrade otp
    verifyUpgradeOTP: async (context, data) => {
      try {
        let formData = new FormData();
        formData.append("useremail", data.useremail);
        formData.append("otpcode", data.otpcode);
        formData.append("env", "web");

        return await fetch(
          `${BASE_URL}/verify-upgradeotp`,
          {
            method: "POST",
            body: formData,
          }
        );
      } catch (e) {
        console.log(e.message);
      }
    },

    //submit user upgrade details
    submitUpgradeDetails: async (context, data) => {
      try {
        let formData = new FormData();
        formData.append("emm", data.emm);
        formData.append("pww", data.pww);
        formData.append("busemail", data.busemail);
        formData.append("busname", data.busname);
        formData.append("biz_phone", data.biz_phone);
        formData.append("desc", data.desc);
        formData.append("env", "web");

        return await fetch(
          `${BASE_URL}/upgradetobiz`,
          {
            method: "POST",
            body: formData,
          }
        );
      } catch (e) {
        console.log(e.message);
      }
    },

    //fetch user payout accounts
    fetchPayoutAccounts: async (context) => {
      try {
        let formData = new FormData();
        formData.append("sesscode", context.getters.getUser.sesscode);
        formData.append("userid", context.getters.getUser.userid);
        formData.append("businessid", context.getters.getUser.business_id);
        formData.append("env", "web");

        return await fetch(
          `${BASE_URL}/payout-accounts`,
          {
            method: "POST",
            body: formData,
          }
        )
          .then((response) => response.json())
          .then((response) => {
            if (response) {
              context.commit("updatePayoutAccount", response);
            }
          })
          .catch((e) => console.log(e.message));
      } catch (e) {
        console.log(e.message);
      }
    },

    //set user payout account
    setPayoutAccount: async (context, data) => {
      try {
        let formData = new FormData();
        formData.append("sesscode", context.getters.getUser.sesscode);
        formData.append("userid", context.getters.getUser.userid);
        formData.append("businessid", context.getters.getUser.business_id);
        formData.append("payoutacnt_id", data.payoutacnt_id);
        formData.append("env", "web");

        return await fetch(
          `${BASE_URL}/change-payoutacct`,
          {
            method: "POST",
            body: formData,
          }
        );
      } catch (e) {
        console.log(e.message);
      }
    },

    //delete user pay out account
    deletePayoutAccount: async (context, data) => {
      try {
        let formData = new FormData();
        formData.append("sesscode", context.getters.getUser.sesscode);
        formData.append("userid", context.getters.getUser.userid);
        formData.append("businessid", context.getters.getUser.business_id);
        formData.append("payoutacnt_id", data.payoutacnt_id);
        formData.append("env", "web");

        return await fetch(
          `${BASE_URL}/deletepayout`,
          {
            method: "POST",
            body: formData,
          }
        );
      } catch (e) {
        console.log(e.message);
      }
    },

    //add a new payout account
    addNewPayAccount: async (context, data) => {
      try {
        let formData = new FormData();
        formData.append("sesscode", context.getters.getUser.sesscode);
        formData.append("userid", context.getters.getUser.userid);
        formData.append("businessid", context.getters.getUser.business_id);
        formData.append("acctno", data.acctno);
        formData.append("acctname", data.acctname);
        formData.append("bankcode", data.bankcode);
        formData.append("bankname", data.bankname);
        formData.append("currency", data.currency);
        formData.append("env", "web");

        return await fetch(
          `${BASE_URL}/add-payoutaccount`,
          {
            method: "POST",
            body: formData,
          }
        );
      } catch (e) {
        console.log(e.message);
      }
    },

    //fetch pay out
    fetchPayout: async (context) => {
      try {
        let formData = new FormData();
        formData.append("sesscode", context.getters.getUser.sesscode);
        formData.append("userid", context.getters.getUser.userid);
        formData.append("businessid", context.getters.getUser.business_id);
        formData.append("env", "web");

        return await fetch(
          `${BASE_URL}/payouts`,
          {
            method: "POST",
            body: formData,
          }
        )
          .then((response) => response.json())
          .then((response) => {
            if (response) {
              context.commit("updatePayout", response);
            }
          })
          .catch((e) => console.log(e.message));
      } catch (e) {
        console.log(e.message);
      }
    },

    //fetch payout detail
    fetchPayoutDetail: async (context, data) => {
      try {
        let formData = new FormData();
        formData.append("sesscode", context.getters.getUser.sesscode);
        formData.append("userid", context.getters.getUser.userid);
        formData.append("businessid", context.getters.getUser.business_id);
        formData.append("payoutref", data.payoutref);
        formData.append("env", "web");

        return await fetch(
          `${BASE_URL}/payoutdetails`,
          {
            method: "POST",
            body: formData,
          }
        )
          .then((response) => response.json())
          .then((response) => {
            if (response) {
              context.commit("updatePayoutDetail", response);
            }
          })
          .catch((e) => console.log(e.message));
      } catch (e) {
        console.log(e.message);
      }
    },

    //fetch user disputes
    fetchDispute: async (context) => {
      try {
        let formData = new FormData();
        formData.append("sesscode", context.getters.getUser.sesscode);
        formData.append("userid", context.getters.getUser.userid);
        formData.append("businessid", context.getters.getUser.business_id);
        formData.append("env", "web");

        return await fetch(
          `${BASE_URL}/disputes`,
          {
            method: "POST",
            body: formData,
          }
        )
          .then((response) => response.json())
          .then((response) => {
            if (response) {
              context.commit("updateDispute", response);
            }
          })
          .catch((e) => console.log(e.message));
      } catch (e) {
        console.log(e.message);
      }
    },

    //decline user dispute
    declineDispute: async (context, data) => {
      try {
        let formData = new FormData();
        formData.append("sesscode", context.getters.getUser.sesscode);
        formData.append("userid", context.getters.getUser.userid);
        formData.append("businessid", context.getters.getUser.business_id);
        formData.append("disputeid", data.disputeid);
        formData.append("txref", data.txref);
        formData.append("name", data.name);
        formData.append("email", data.email);
        formData.append("phone", data.phone);
        formData.append("claim", data.claim);
        formData.append("descres", data.descres);
        formData.append("receipt", data.receipt);
        formData.append("env", "web");

        return await fetch(
          `${BASE_URL}/decline-disputes`,
          {
            method: "POST",
            body: formData,
          }
        );
      } catch (e) {
        console.log(e.message);
      }
    },

    //accept user dispute
    acceptDispute: async (context, data) => {
      try {
        let formData = new FormData();
        formData.append("sesscode", context.getters.getUser.sesscode);
        formData.append("userid", context.getters.getUser.userid);
        formData.append("businessid", context.getters.getUser.business_id);
        formData.append("disputeid", data.disputeid);
        formData.append("transref", data.transref);
        formData.append("customername", data.customername);
        formData.append("customeremail", data.customeremail);
        formData.append("customerphone", data.customerphone);
        formData.append("dclaim", data.dclaim);
        formData.append("torefund", data.torefund);
        formData.append("env", "web");

        return await fetch(
          `${BASE_URL}/accept-disputes`,
          {
            method: "POST",
            body: formData,
          }
        );
      } catch (e) {
        console.log(e.message);
      }
    },

    //verify bvn new
    verifyBvnNew: async (context, data) => {
      try {
        let formData = new FormData();
        formData.append("sesscode", context.getters.getUser.sesscode);
        formData.append("userid", context.getters.getUser.userid);
        formData.append("businessid", context.getters.getUser.business_id);
        formData.append("busadr", data.busadr);
        formData.append("bvnn", data.bvnn);
        formData.append("env", "web");

        return await fetch(
          `${BASE_URL}/verifyme2`,
          {
            method: "POST",
            body: formData,
          }
        );
      } catch (e) {
        console.log(e.message);
      }
    },

    //complete bvn registration
    validateBVN: async (context, data) => {
      try {
        let formData = new FormData();
        formData.append("sesscode", context.getters.getUser.sesscode);
        formData.append("userid", context.getters.getUser.userid);
        formData.append(
          "businessid",
          sessionStorage.new_business_id || context.getters.getUser.business_id
        );
        formData.append("busadr", data.busadr);
        formData.append("bvnn", data.bvnn);
        formData.append("otp", data.otp);
        formData.append("vertype", String(data.vertype || "BVN").toUpperCase());
        formData.append("env", "web");
        formData.append("desc", data.desc);
        formData.append("lga", data.lga);
        formData.append("state", data.state);
        formData.append("city", data.city);

        return await fetch(
          `${BASE_URL}/validatebvotp`,
          {
            method: "POST",
            body: formData,
          }
        );
      } catch (e) {
        console.log(e.message);
      }
    },

    //update team member
    UpdateTeamMemberStatus: async (context, data) => {
      try {
        let formData = new FormData();
        formData.append("sesscode", context.getters.getUser.sesscode);
        formData.append("userid", context.getters.getUser.userid);
        formData.append("businessid", context.getters.getUser.business_id);
        formData.append("updstatus", data.updstatus);
        formData.append("teamid", data.teamid);
        formData.append("env", "web");

        return await fetch(
          `${BASE_URL}/updateteam`,
          {
            method: "POST",
            body: formData,
          }
        );
      } catch (e) {
        console.log(e.message);
      }
    },

    //fetch bank account
    fetchBankAccounts: async (context) => {
      try {
        const currency = `${context.getters.getCurrency || ""}`
          .toString()
          .toUpperCase();
        const supportedForeignCurrencies = ["USD", "CAD", "GBP", "EUR"];
        const payload = {
          rawUrl: `${BASE_URL}/bankaccounts.php`,
          caches: [
            { key: "banklist", value: "banklist" },
            { key: "usddata", value: "usddata" },
            { key: "company_accounts", value: "company_accounts" },
            { key: "customer_accounts", value: "customer_accounts" },
            { key: "manualtransfer_logs", value: "manualtransfer_logs" },
          ],
        };

        if (supportedForeignCurrencies.includes(currency)) {
          payload.data = { currency };
        }

        const response = await context.dispatch("httpRequest", payload);
        if (response?.status) {
          const existing = context.getters.getBankAccounts || {};
          const normalized = { ...existing, ...response };
          if (response?.data && typeof response.data === "object" && !Array.isArray(response.data)) {
            const account = normalizeBankAccountRecord(
              response.data,
              currency || response.data.currency || "USD"
            );
            if (supportedForeignCurrencies.includes(currency)) {
              normalized.usddata = [account];
              normalized.company_accounts = [account];
              normalized.customer_accounts = [];
              normalized.banklist = Array.isArray(existing.banklist)
                ? existing.banklist
                : Array.isArray(response.banklist)
                  ? response.banklist
                  : [];
            } else {
              normalized.banklist = Array.isArray(response.banklist) ? response.banklist : [];
            }
          } else {
            normalized.banklist = Array.isArray(response.banklist)
              ? response.banklist
              : Array.isArray(existing.banklist)
                ? existing.banklist
                : [];
          }
          context.commit("updateBankAccount", normalized);
        }
        return response;
      } catch (e) {
        console.log(e.message);
      }
    },

    //submit new business
    addBusiness: async (context, data) => {
      try {
        const regMeta = resolveRegOnboardingMeta(context.state, context.getters);
        const user = context.getters.getUser || {};
        const sesscode = user.sesscode || regMeta.sesscode || "";
        const userid = user.userid || regMeta.userid || "";
        const businessid =
          user.business_id ||
          regMeta.source_business_id ||
          regMeta.business_id ||
          regMeta.businessid ||
          "";
        let formData = new FormData();
        formData.append("sesscode", sesscode);
        formData.append("userid", userid);
        formData.append("businessid", businessid);
        formData.append("busemail", data.busemail);
        formData.append("busname", data.busname);
        formData.append("busphone", data.busphone);
        formData.append("desc", data.desc);
        formData.append("env", "web");

        return await fetch(
          `${BASE_URL}/addnewbiz`,
          {
            method: "POST",
            body: formData,
          }
        );
      } catch (e) {
        console.log(e.message);
      }
    },

    //verify new business
    verifyNewBusiness: async (context, data) => {
      try {
        let formData = new FormData();
        formData.append("sesscode", context.getters.getUser.sesscode);
        formData.append("userid", context.getters.getUser.userid);
        formData.append("businessid", context.getters.getUser.business_id);
        formData.append("newbizid", data.newbizid);
        formData.append("bvnn", data.bvnn);
        formData.append("city", data.city);
        formData.append("state", data.state);
        formData.append("busadr", data.busadr);
        formData.append("env", "web");

        return await fetch(
          `${BASE_URL}/verifynewbiz`,
          {
            method: "POST",
            body: formData,
          }
        );
      } catch (e) {
        console.log(e.message);
      }
    },

    //add new invoice
    newInvoice: async (context, data) => {
      try {
        let formData = new FormData();
        formData.append("sesscode", context.getters.getUser.sesscode);
        formData.append("userid", context.getters.getUser.userid);
        formData.append("businessid", context.getters.getUser.business_id);
        formData.append("customer_email", data.customer_email);
        formData.append("unitprice", data.unitprice);
        formData.append("quantity", data.quantity);
        formData.append("duedate", data.duedate);
        formData.append("currency", data.currency);
        formData.append("item", data.item);
        formData.append("desc", data.desc);
        formData.append("env", "web");

        return await fetch(
          `${BASE_URL}/create-invoice`,
          {
            method: "POST",
            body: formData,
          }
        );
      } catch (e) {
        console.log(e.message);
      }
    },

    //reset user pin
    resetPin: async (context) => {
      try {
        let formData = new FormData();
        formData.append("sesscode", context.getters.getUser.sesscode);
        formData.append("userid", context.getters.getUser.userid);
        formData.append("businessid", context.getters.getUser.business_id);
        formData.append("env", "web");

        return await fetch(
          `${BASE_URL}/reset-pin`,
          {
            method: "POST",
            body: formData,
          }
        );
      } catch (e) {
        console.log(e.message);
      }
    },

    //confirm reset pin
    confirmResetPin: async (context, data) => {
      try {
        let formData = new FormData();
        formData.append("sesscode", context.getters.getUser.sesscode);
        formData.append("userid", context.getters.getUser.userid);
        formData.append("businessid", context.getters.getUser.business_id);
        formData.append("pinotp", data.pinotp);
        formData.append("newpin", data.newpin);
        formData.append("confirmpin", data.confirmpin);
        formData.append("channel", data.channel || "");
        formData.append("env", "web");

        return await fetch(
          `${BASE_URL}/confirm-pinreset`,
          {
            method: "POST",
            body: formData,
          }
        );
      } catch (e) {
        console.log(e.message);
      }
    },

    //generate payment link excel
    generatePaymentExcel: async (context, data) => {
      try {
        let formData = new FormData();
        formData.append("sesscode", context.getters.getUser.sesscode);
        formData.append("userid", context.getters.getUser.userid);
        formData.append("businessid", context.getters.getUser.business_id);
        formData.append("pageid", data.pageid);
        formData.append("pagetype", data.pagetype);
        formData.append("env", "web");

        return await fetch(
          `${BASE_URL}/pagetrans-excel`,
          {
            method: "POST",
            body: formData,
          }
        );
      } catch (e) {
        console.log(e.message);
      }
    },

    //fetch referrers
    fetchReferrers: async (context) => {
      try {
        let formData = new FormData();
        formData.append("sesscode", context.getters.getUser.sesscode);
        formData.append("userid", context.getters.getUser.userid);
        formData.append("businessid", context.getters.getUser.business_id);
        formData.append("env", "web");

        await fetch(
          `${BASE_URL}/referrals`,
          {
            method: "POST",
            body: formData,
          }
        )
          .then((response) => response.json())
          .then((response) => {
            if (response.status)
              return context.commit("updateReferrer", response.data);
          });
      } catch (e) {
        console.log(e.message);
      }
    },

    //fetch two factor details
    /*    fetchTwoFactor: async (context) => {
      try {
        let formData = new FormData();
        formData.append("userid", context.getters.getUserId);
        await fetch(
          `${BASE_URL}/mfasetup`,
          {
            method: "POST",
            body: formData,
          }
        )
          .then((response) => response.json())
          .then((response) => {
            if (response.status)
              return context.commit("updateTwoFactor", response);
          });
      } catch (e) {
        console.log(e.message);
      }
    }, */

    fetchTwoFactor: async (context) => {
      try {
        let formData = new FormData();
        formData.append("userid", context.getters.getUser.userid);
        formData.append("env", "web");

        await fetch(
          `${BASE_URL}/mfasetup`,
          {
            method: "POST",
            body: formData,
          }
        )
          .then((response) => response.json())
          .then((response) => {
            if (response.status)
              return context.commit("updateTwoFactor", response);
          });
      } catch (e) {
        console.log(e.message);
      }
    },

    //complete 2fa setup
    completeTwoFactor: async (context, data) => {
      try {
        let formData = new FormData();
        formData.append("userid", context.getters.getUser.userid);
        formData.append("secretcode", data.secretcode);
        formData.append("hiscode", data.code);
        formData.append("env", "web");
        formData.append("platform", "bolddweb");

        return await fetch(
          `${BASE_URL}/validate2faweb`,
          {
            method: "POST",
            body: formData,
          }
        );
      } catch (e) {
        console.log(e.message);
      }
    },

    //remove 2fa setup
    DeleteTwoFactor: async (context) => {
      try {
        let formData = new FormData();
        formData.append("sesscode", context.getters.getUser.sesscode);
        formData.append("userid", context.getters.getUser.userid);
        formData.append("businessid", context.getters.getUser.business_id);
        formData.append("env", "web");

        return await fetch(
          `${BASE_URL}/remove2fa`,
          {
            method: "POST",
            body: formData,
          }
        );
      } catch (e) {
        console.log(e.message);
      }
    },

    //fetch beneficiary list
    fetchBeneficiaryList: async (context) => {
      try {
        let formData = new FormData();
        formData.append("sesscode", context.getters.getUser.sesscode);
        formData.append("userid", context.getters.getUser.userid);
        formData.append("businessid", context.getters.getUser.business_id);
        formData.append("env", "web");

        await fetch(
          `${BASE_URL}/beneficiaries`,
          {
            method: "POST",
            body: formData,
          }
        )
          .then((response) => response.json())
          .then((response) => {
            if (response.status)
              return context.commit("updateBeneficiaryList", response);
          });
      } catch (e) {
        console.log(e.message);
      }
    },

    //add new beneficiary
    addBeneficiary: async (context, data) => {
      try {
        let formData = new FormData();
        formData.append("sesscode", context.getters.getUser.sesscode);
        formData.append("userid", context.getters.getUser.userid);
        formData.append("businessid", context.getters.getUser.business_id);
        formData.append("acctno", data.acctno);
        formData.append("beneficiary_name", data.acctname);
        formData.append("bankcode", data.bankcode);
        formData.append("bankname", data.bankname);
        formData.append("currency", data.currency);
        formData.append("env", "web");

        return await fetch(
          `${BASE_URL}/add-beneficiary`,
          {
            method: "POST",
            body: formData,
          }
        );
      } catch (e) {
        console.log(e.message);
      }
    },

    //remove beneficiary
    removeBeneficiary: async (context, data) => {
      try {
        let formData = new FormData();
        formData.append("sesscode", context.getters.getUser.sesscode);
        formData.append("userid", context.getters.getUser.userid);
        formData.append("businessid", context.getters.getUser.business_id);
        formData.append("beneficiaryid", data.beneficiaryid);
        formData.append("env", "web");

        return await fetch(
          `${BASE_URL}/remove-beneficiary`,
          {
            method: "POST",
            body: formData,
          }
        );
      } catch (e) {
        console.log(e.message);
      }
    },

    //make bulk payment
    bulkPayment: async (context, data) => {
      try {
        let formData = new FormData();
        formData.append("sesscode", context.getters.getUser.sesscode);
        formData.append("userid", context.getters.getUser.userid);
        formData.append("businessid", context.getters.getUser.business_id);
        formData.append("paytype", data.paytype);
        formData.append("data", JSON.stringify(data.data));
        formData.append("paymentname", data.paymentname);
        formData.append("ctoken", data.ctoken);
        formData.append("env", "web");

        return await fetch(
          `${BASE_URL}/bulkpay`,
          {
            method: "POST",
            body: formData,
          }
        );
      } catch (e) {
        console.log(e.message);
      }
    },

    //fetch wallet
    fetchWallet: async (context) => {
      await context.dispatch("httpRequest", {
        url: "mywallet",
        caches: [{ key: "mywallet", value: "mywallet" }],
      });
    },

    //schedule payment
    schedulePayment: async (context, data) => {
      try {
        let formData = new FormData();
        formData.append("sesscode", context.getters.getUser.sesscode);
        formData.append("userid", context.getters.getUser.userid);
        formData.append("businessid", context.getters.getUser.business_id);
        formData.append("producttype", data.producttype);
        formData.append("schedulemode", data.schedulemode);
        formData.append("startdate", data.startdate);
        formData.append("enddate", data.enddate);
        formData.append("start_time", data.start_time);
        formData.append("frequency", data.frequency);
        formData.append("amount", data.amount);
        formData.append("desc", data.desc);
        formData.append("acctno", data.acctno);
        formData.append("acctname", data.acctname);
        formData.append("bankcode", data.bankcode);
        formData.append("bankname", data.bankname);
        formData.append("currency", data.currency);
        formData.append("ctoken", data.ctoken);
        formData.append("env", "web");

        return await fetch(
          `${BASE_URL}/scheduler`,
          {
            method: "POST",
            body: formData,
          }
        );
      } catch (e) {
        console.log(e.message);
      }
    },

    //fetch scheduled payments
    fetchScheduledPayments: async (context) => {
      try {
        let formData = new FormData();
        formData.append("sesscode", context.getters.getUser.sesscode);
        formData.append("userid", context.getters.getUser.userid);
        formData.append("businessid", context.getters.getUser.business_id);
        formData.append("env", "web");

        await fetch(
          `${BASE_URL}/schedulelist`,
          {
            method: "POST",
            body: formData,
          }
        )
          .then((response) => response.json())
          .then((response) => {
            if (response.status)
              return context.commit("updateSchedule", response.data);
          });
      } catch (e) {
        console.log(e.message);
      }
    },

    //fetch employees
    fetchEmployees: async (context) => {
      try {
        let formData = new FormData();
        formData.append("sesscode", context.getters.getUser.sesscode);
        formData.append("userid", context.getters.getUser.userid);
        formData.append("businessid", context.getters.getUser.business_id);
        formData.append("env", "web");

        await fetch(
          `${BASE_URL}/employeelist`,
          {
            method: "POST",
            body: formData,
          }
        )
          .then((response) => response.json())
          .then((response) => {
            if (response.status)
              return context.commit("updateEmployee", response.data);
          });
      } catch (e) {
        console.log(e.message);
      }
    },

    //add new employee
    addNewEmployee: async (context, data) => {
      try {
        let formData = new FormData();
        formData.append("sesscode", context.getters.getUser.sesscode);
        formData.append("userid", context.getters.getUser.userid);
        formData.append("businessid", context.getters.getUser.business_id);
        formData.append("acctno", data.acctno);
        formData.append("employee_name", data.employee_name);
        formData.append("bankcode", data.bankcode);
        formData.append("bankname", data.bankname);
        formData.append("pension_amount", data.pension_amount);
        formData.append("pension_manager", data.pension_manager);
        formData.append("salary_amount", data.salary_amount);
        formData.append("rsano", data.rsano);
        formData.append("staffno", data.staffno);
        formData.append("tax_amount", data.tax_amount);
        formData.append("tin", data.tin);
        formData.append("env", "web");

        return await fetch(
          `${BASE_URL}/addemployee`,
          {
            method: "POST",
            body: formData,
          }
        );
      } catch (e) {
        console.log(e.message);
      }
    },

    //edit employee
    editEmployee: async (context, data) => {
      try {
        let formData = new FormData();
        formData.append("sesscode", context.getters.getUser.sesscode);
        formData.append("userid", context.getters.getUser.userid);
        formData.append("businessid", context.getters.getUser.business_id);
        formData.append("empid", data.empid);
        formData.append("acctno", data.acctno);
        formData.append("employee_name", data.employee_name);
        formData.append("bankcode", data.bankcode);
        formData.append("bankname", data.bankname);
        formData.append("pension_amount", data.pension_amount);
        formData.append("pension_manager", data.pension_manager);
        formData.append("salary_amount", data.salary_amount);
        formData.append("rsano", data.rsano);
        formData.append("staffno", data.staffno);
        formData.append("tax_amount", data.tax_amount);
        formData.append("tin", data.tin);
        formData.append("env", "web");

        return await fetch(
          `${BASE_URL}/editemployee`,
          {
            method: "POST",
            body: formData,
          }
        );
      } catch (e) {
        console.log(e.message);
      }
    },

    //add new schedule history
    fetchScheduleHistory: async (context, data) => {
      try {
        let formData = new FormData();
        formData.append("sesscode", context.getters.getUser.sesscode);
        formData.append("userid", context.getters.getUser.userid);
        formData.append("businessid", context.getters.getUser.business_id);
        formData.append("reference", data.reference);
        formData.append("transid", data.transid);
        formData.append("env", "web");
        await fetch(
          `${BASE_URL}/schedulehistory`,
          {
            method: "POST",
            body: formData,
          }
        )
          .then((response) => response.json())
          .then((response) => {
            if (response.status)
              return context.commit("updateScheduleHistory", response);
          });
      } catch (e) {
        console.log(e.message);
      }
    },

    //import employees
    importEmployees: async (context, data) => {
      try {
        let formData = new FormData();
        formData.append("sesscode", context.getters.getUser.sesscode);
        formData.append("userid", context.getters.getUser.userid);
        formData.append("businessid", context.getters.getUser.business_id);
        formData.append("file", data.file);
        formData.append("env", "web");

        return await fetch(
          `${BASE_URL}/importemployee`,
          {
            method: "POST",
            body: formData,
          }
        );
      } catch (e) {
        console.log(e.message);
      }
    },

    //change  scheduled payments
    ChangeScheduledPayments: async (context, data) => {
      try {
        let formData = new FormData();
        formData.append("sesscode", context.getters.getUser.sesscode);
        formData.append("userid", context.getters.getUser.userid);
        formData.append("businessid", context.getters.getUser.business_id);
        formData.append("dataid", JSON.stringify(data.data));
        formData.append("env", "web");
        console.log(data);

        let endpoint = "";
        if (data.mode === "enable") endpoint = "enableschedule";
        if (data.mode === "disable") endpoint = "disableschedule";
        if (data.mode === "remove") endpoint = "remove-schedule";

        return await fetch(
          `${BASE_URL}/${endpoint}`,
          {
            method: "POST",
            body: formData,
          }
        );
      } catch (e) {
        console.log(e.message);
      }
    },

    //fetch customer details
    FetchCustomerDetails: async (context, data) => {
      try {
        let formData = new FormData();
        formData.append("sesscode", context.getters.getUser.sesscode);
        formData.append("userid", context.getters.getUser.userid);
        formData.append("businessid", context.getters.getUser.business_id);
        formData.append("customerid", data.id);
        formData.append("env", "web");

        await fetch(
          `${BASE_URL}/customertrans`,
          {
            method: "POST",
            body: formData,
          }
        )
          .then((response) => response.json())
          .then((response) => {
            if (response.status)
              return context.commit("updateCustomerDetails", response);
          });
      } catch (e) {
        console.log(e.message);
      }
    },

    //add new customer
    newCustomer: async (context, data) => {
      try {
        let formData = new FormData();
        formData.append("sesscode", context.getters.getUser.sesscode);
        formData.append("userid", context.getters.getUser.userid);
        formData.append("businessid", context.getters.getUser.business_id);
        formData.append("fname", data.fname);
        formData.append("lname", data.lname);
        formData.append("email", data.email);
        formData.append("phn", data.phn);
        formData.append("env", "web");

        return await fetch(
          `${BASE_URL}/addcustomer`,
          {
            method: "POST",
            body: formData,
          }
        );
      } catch (e) {
        console.log(e.message);
      }
    },

    //requery ledger transaction
    queryLedgerTrans: async (context, data) => {
      try {
        let formData = new FormData();
        formData.append("sesscode", context.getters.getUser.sesscode);
        formData.append("userid", context.getters.getUser.userid);
        formData.append("businessid", context.getters.getUser.business_id);
        formData.append("reference", data.reference);
        formData.append("env", "web");

        return await fetch(
          `${BASE_URL}/transrequery`,
          {
            method: "POST",
            body: formData,
          }
        );
      } catch (e) {
        console.log(e.message);
      }
    },

    // toggle blacklisting for a  customer
    blackListCustomer: async (context, data) => {
      try {
        let formData = new FormData();
        formData.append("sesscode", context.getters.getUser.sesscode);
        formData.append("userid", context.getters.getUser.userid);
        formData.append("businessid", context.getters.getUser.business_id);
        formData.append("customer_email", data.customer_email);
        formData.append("env", "web");

        let endpoint =
          data.mode === "blacklist" ? "blacklistcust" : "unblacklistcust";

        return await fetch(
          `${BASE_URL}/${endpoint}`,
          {
            method: "POST",
            body: formData,
          }
        );
      } catch (e) {
        console.log(e.message);
      }
    },

    //upload bulk payment excel file
    uploadPaymentDetails: async (context, data) => {
      try {
        let formData = new FormData();
        formData.append("sesscode", context.getters.getUser.sesscode);
        formData.append("userid", context.getters.getUser.userid);
        formData.append("businessid", context.getters.getUser.business_id);
        formData.append("file", data.file);
        formData.append("productype", data.productype);
        formData.append("env", "web");
        return await new Promise((resolve, reject) => {
          const xhr = new XMLHttpRequest();
          xhr.open(
            "POST",
            `${BASE_URL}/importbulkpay`
          );
          xhr.timeout = 930000;

          if (typeof data.onUploadProgress === "function") {
            xhr.upload.onprogress = data.onUploadProgress;
          }

          xhr.onload = () => {
            resolve({
              ok: xhr.status >= 200 && xhr.status < 300,
              status: xhr.status,
              json: async () => {
                try {
                  return JSON.parse(xhr.responseText || "{}");
                } catch (e) {
                  const rawResponse = String(xhr.responseText || "").replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();
                  return {
                    status: false,
                    message: rawResponse !== ""
                      ? rawResponse.slice(0, 220)
                      : "The upload response could not be read. Please retry with a smaller file if this keeps happening.",
                  };
                }
              },
              text: async () => xhr.responseText || "",
            });
          };

          xhr.onerror = () => {
            reject(new Error("Upload failed. Please check your network connection and try again."));
          };

          xhr.ontimeout = () => {
            reject(new Error("Upload validation is taking too long. Please retry or reduce the number of rows."));
          };

          xhr.send(formData);
        });
      } catch (e) {
        console.log(e.message);
      }
    },

    validateBatchTransferRow: async (context, data) => {
      try {
        let formData = new FormData();
        formData.append("sesscode", context.getters.getUser.sesscode);
        formData.append("userid", context.getters.getUser.userid);
        formData.append("businessid", context.getters.getUser.business_id);
        formData.append("line_no", data.line_no || "");
        formData.append("account_number", data.account_number || "");
        formData.append("account_name", data.entered_account_name || data.account_name || "");
        formData.append("bank_name", data.bank_name || "");
        formData.append("bank_code", data.bank_code || "");
        formData.append("amount", data.amount || "");
        formData.append("description", data.description || "");
        formData.append("env", "web");

        return await fetch(
          `${BASE_URL}/validatebatchpayrow`,
          {
            method: "POST",
            body: formData,
          }
        );
      } catch (e) {
        console.log(e.message);
      }
    },

    submitBatchTransfer: async (context, data) => {
      try {
        let formData = new FormData();
        formData.append("sesscode", context.getters.getUser.sesscode);
        formData.append("userid", context.getters.getUser.userid);
        formData.append("businessid", context.getters.getUser.business_id);
        formData.append("data", JSON.stringify(data.data || []));
        formData.append("transpin", data.transpin || "");
        formData.append("batch_name", data.batch_name || "");
        formData.append("source_file_name", data.source_file_name || "");
        formData.append("notification_every", data.notification_every || 0);
        formData.append("env", "web");

        return await fetch(
          `${BASE_URL}/submitbatchtransfer`,
          {
            method: "POST",
            body: formData,
          }
        );
      } catch (e) {
        console.log(e.message);
      }
    },

    fetchBatchTransferHistory: async (context, data = {}) => {
      try {
        let formData = new FormData();
        formData.append("sesscode", context.getters.getUser.sesscode);
        formData.append("userid", context.getters.getUser.userid);
        formData.append("businessid", context.getters.getUser.business_id);
        formData.append("limit", data.limit || 20);
        formData.append("env", "web");

        return await safeJsonFetch(
          `${BASE_URL}/batchtransferhistory`,
          {
            method: "POST",
            body: formData,
          },
          "Unable to load batch transfer history right now. Please try again."
        );
      } catch (e) {
        console.log(e.message);
      }
    },

    fetchBatchTransferDetails: async (context, data) => {
      try {
        let formData = new FormData();
        formData.append("sesscode", context.getters.getUser.sesscode);
        formData.append("userid", context.getters.getUser.userid);
        formData.append("businessid", context.getters.getUser.business_id);
        formData.append("batch_ref", data.batch_ref || "");
        formData.append("env", "web");

        return await safeJsonFetch(
          `${BASE_URL}/batchtransferdetails`,
          {
            method: "POST",
            body: formData,
          },
          "Unable to load this batch transfer right now. Please check your connection and try again."
        );
      } catch (e) {
        console.log(e.message);
      }
    },

    retryBatchTransfer: async (context, data) => {
      try {
        let formData = new FormData();
        formData.append("sesscode", context.getters.getUser.sesscode);
        formData.append("userid", context.getters.getUser.userid);
        formData.append("businessid", context.getters.getUser.business_id);
        formData.append("batch_ref", data.batch_ref || "");
        formData.append("transpin", data.transpin || "");
        formData.append("env", "web");

        return await safeJsonFetch(
          `${BASE_URL}/retrybatchtransfer`,
          {
            method: "POST",
            body: formData,
          },
          "Unable to retry this batch transfer right now. Please check your connection and try again."
        );
      } catch (e) {
        console.log(e.message);
      }
    },

    //fetch data  for bulk payment plan
    fetchBulkDataPlans: async (context, data) => {
      try {
        const formData = new FormData();
        formData.append("sesscode", context.getters.getUser.sesscode);
        formData.append("userid", context.getters.getUser.userid);
        formData.append("businessid", context.getters.getUser.business_id);
        formData.append("provider", data.provider);
        formData.append("env", "web");

        return await fetch(
          `${BASE_URL}/getdataplans`,
          {
            method: "POST",
            body: formData,
          }
        );
      } catch (e) {
        console.log(e.message);
      }
    },

    //fetch cac registrations
    fetchUserCAC: async (context) => {
      try {
        const formData = new FormData();
        formData.append("sesscode", context.getters.getUser.sesscode);
        formData.append("userid", context.getters.getUser.userid);
        formData.append("businessid", context.getters.getUser.business_id);
        formData.append("env", "web");

        await fetch(
          `${BASE_URL}/mycacreg`,
          {
            method: "POST",
            body: formData,
          }
        )
          .then((response) => response.json())
          .then((response) => {
            if (response.status) return context.commit("updateCAC", response);
            return context.commit("updateCAC", "");
          })
          .catch((e) => console.log(e.message));
      } catch (e) {
        console.log(e.message);
      }
    },

    payCAC: async (context, data) => {
      try {
        const formData = new FormData();
        formData.append("sesscode", context.getters.getUser.sesscode);
        formData.append("userid", context.getters.getUser.userid);
        formData.append("businessid", context.getters.getUser.business_id);
        formData.append("regmode", data.regmode);
        formData.append("env", "web");

        return await fetch(
          `${BASE_URL}/payforcac`,
          {
            method: "POST",
            body: formData,
          }
        );
      } catch (e) {
        console.log(e.message);
      }
    },

    regCACBusinessInfo: async (context, data) => {
      try {
        const formData = new FormData();
        formData.append("sesscode", context.getters.getUser.sesscode);
        formData.append("userid", context.getters.getUser.userid);
        formData.append("businessid", context.getters.getUser.business_id);
        formData.append("regid", context.getters.getCacReg.regid);
        formData.append("reference", data.reference);
        formData.append("proposed_name1", data.proposed_name1);
        formData.append("proposed_name2", data.proposed_name2);
        formData.append("proposed_name3", data.proposed_name3);
        formData.append("business_nature", data.business_nature);
        formData.append("bizcommence_date", data.bizcommence_date);
        formData.append("env", "web");

        return await fetch(
          `${BASE_URL}/cacbizreg`,
          {
            method: "POST",
            body: formData,
          }
        );
      } catch (e) {
        console.log(e.message);
      }
    },

    regCACBusinessAddress: async (context, data) => {
      try {
        const formData = new FormData();
        formData.append("sesscode", context.getters.getUser.sesscode);
        formData.append("userid", context.getters.getUser.userid);
        formData.append("businessid", context.getters.getUser.business_id);
        formData.append("regid", context.getters.getCacReg.regid);
        formData.append("business_adr", data.business_adr);
        formData.append("business_state", data.business_state);
        formData.append("business_city", data.business_city);
        formData.append("business_adr2", data.business_adr2);
        formData.append("business_state2", data.business_state2);
        formData.append("business_city2", data.business_city2);
        formData.append("env", "web");

        return await fetch(
          `${BASE_URL}/cacreg-address`,
          {
            method: "POST",
            body: formData,
          }
        );
      } catch (e) {
        console.log(e.message);
      }
    },

    regCACProprietorInfo: async (context, data) => {
      try {
        const formData = new FormData();
        formData.append("sesscode", context.getters.getUser.sesscode);
        formData.append("userid", context.getters.getUser.userid);
        formData.append("businessid", context.getters.getUser.business_id);
        formData.append("regid", context.getters.getCacReg.regid);
        formData.append("owner_passport", data.owner_passport);
        formData.append("owner_sname", data.owner_sname);
        formData.append("owner_fname", data.owner_fname);
        formData.append("owner_oname", data.owner_oname);
        formData.append("owner_nationality", data.owner_nationality);
        formData.append("former_nationality", data.former_nationality);
        formData.append("owner_gender", data.owner_gender);
        formData.append("owner_dob", data.owner_dob);
        formData.append("idcardno", data.idcardno);
        formData.append("owner_phone", data.owner_phone);
        formData.append("owner_email", data.owner_email);
        formData.append("idtype", data.idtype);
        formData.append("idcard", data.idcard);
        formData.append("occupation", data.occupation);
        formData.append("env", "web");

        return await fetch(
          `${BASE_URL}/cacreg-proprietors`,
          {
            method: "POST",
            body: formData,
          }
        );
      } catch (e) {
        console.log(e.message);
      }
    },

    regCACProprietorAddress: async (context, data) => {
      try {
        const formData = new FormData();
        formData.append("sesscode", context.getters.getUser.sesscode);
        formData.append("userid", context.getters.getUser.userid);
        formData.append("businessid", context.getters.getUser.business_id);
        formData.append("regid", context.getters.getCacReg.regid);
        formData.append("owner_adr", data.owner_adr);
        formData.append("owner_state", data.owner_state);
        formData.append("owner_city", data.owner_city);
        formData.append("owner_signature", data.owner_signature);
        formData.append("env", "web");

        return await fetch(
          `${BASE_URL}/cacproprietor-addr`,
          {
            method: "POST",
            body: formData,
          }
        );
      } catch (e) {
        console.log(e.message);
      }
    },

    accountStatement: async (context, data) => {
      try {
        const formData = new FormData();
        formData.append("sesscode", context.getters.getUser.sesscode);
        formData.append("userid", context.getters.getUser.userid);
        formData.append("businessid", context.getters.getUser.business_id);
        formData.append("datefrom", data.date_from);
        formData.append("dateto", data.endto);
        if (data.filetype) formData.append("filetype", data.filetype);
        if (data.sendteam !== undefined) formData.append("sendteam", data.sendteam);
        if (Array.isArray(data.teamids) && data.teamids.length > 0) {
          formData.append("teamids", JSON.stringify(data.teamids));
        }
        formData.append("env", "web");

        return await safeJsonOrFileFetch(
          `${BASE_URL}/statement-download`,
          {
            method: "POST",
            body: formData,
          },
          "Account statement could not be generated right now. Please try again shortly.",
          `account-statement-${Date.now()}`
        );
      } catch (e) {
        console.log(e.message);
      }
    },

    generateAccount: async (context, data) => {
      try {
        const formData = new FormData();
        formData.append("sesscode", context.getters.getUser.sesscode);
        formData.append("userid", context.getters.getUser.userid);
        formData.append("businessid", context.getters.getUser.business_id);
        formData.append("bkcode", data.bankcode);
        formData.append("env", "web");

        return await fetch(`${BASE_URL}/generate-account`, {
          method: "POST",
          body: formData,
        });
      } catch (e) {
        console.log(e.message);
      }
    },

    //fetch branches
    fetchBranches: async (context) => {
      await context.dispatch("httpRequest", {
        url: "mybranches",
        caches: [{ key: "branches", value: "data" }],
      });
    },

    fetchBusinessBanks: async (context) => {
      try {
        const formData = new FormData();
        formData.append("sesscode", context.getters.getUser.sesscode);
        formData.append("userid", context.getters.getUser.userid);
        formData.append("businessid", context.getters.getUser.business_id);
        formData.append("env", "web");

        await fetch(
          `${BASE_URL}/listbizbank`,
          {
            method: "POST",
            body: formData,
          }
        )
          .then((response) => response.json())
          .then((response) => {
            if (response.status)
              return context.commit("updateBusinessBanks", response);
          })
          .catch((e) => console.log(e.message));
      } catch (e) {
        console.log(e.message);
      }
    },

    addNewBranch: async (context, data) => {
      try {
        const formData = new FormData();
        formData.append("sesscode", context.getters.getUser.sesscode);
        formData.append("userid", context.getters.getUser.userid);
        formData.append("businessid", context.getters.getUser.business_id);
        formData.append("branchname", data.name);
        formData.append("state", data.state);
        formData.append("branchphone", data.phone);
        formData.append("env", "web");

        return await fetch(
          `${BASE_URL}/createbranch`,
          {
            method: "POST",
            body: formData,
          }
        );
      } catch (e) {
        console.log(e.message);
      }
    },

    fetchCashierTransactions: async (context, data) => {
      try {
        const formData = new FormData();
        formData.append("sesscode", context.getters.getUser.sesscode);
        formData.append("userid", context.getters.getUser.userid);
        formData.append("businessid", context.getters.getUser.business_id);
        formData.append("branchid", data.branchid);
        formData.append("env", "web");

        await fetch(
          `${BASE_URL}/cashierpayn`,
          {
            method: "POST",
            body: formData,
          }
        )
          .then((response) => response.json())
          .then((response) => {
            if (response.status)
              return context.commit("updateCashierTransactions", response);
          })
          .catch((e) => console.log(e.message));
      } catch (e) {
        console.log(e.message);
      }
    },

    confirmPayment: async (context, data) => {
      try {
        const formData = new FormData();
        formData.append("sesscode", context.getters.getUser.sesscode);
        formData.append("userid", context.getters.getUser.userid);
        formData.append("businessid", context.getters.getUser.business_id);
        formData.append("transref", data.reference);
        formData.append("env", "web");

        return await fetch(
          `${BASE_URL}/confirmpay`,
          {
            method: "POST",
            body: formData,
          }
        );
      } catch (e) {
        console.log(e.message);
      }
    },

    mapUser: async (context, data) => {
      try {
        const formData = new FormData();
        formData.append("sesscode", context.getters.getUser.sesscode);
        formData.append("userid", context.getters.getUser.userid);
        formData.append("businessid", context.getters.getUser.business_id);
        formData.append("branchid", data.branchid);
        formData.append("teamid", data.teamid);
        formData.append("env", "web");

        return await fetch(
          `${BASE_URL}/addbranchuser`,
          {
            method: "POST",
            body: formData,
          }
        );
      } catch (e) {
        console.log(e.message);
      }
    },

    generateBranchAccount: async (context, data) => {
      try {
        const formData = new FormData();
        formData.append("sesscode", context.getters.getUser.sesscode);
        formData.append("userid", context.getters.getUser.userid);
        formData.append("businessid", context.getters.getUser.business_id);
        formData.append("branchid", data.branchid);
        formData.append("bkcode", data.bkcode);
        formData.append("env", "web");

        return await fetch(
          `${BASE_URL}/addbranch-acctno`,
          {
            method: "POST",
            body: formData,
          }
        );
      } catch (e) {
        console.log(e.message);
      }
    },

    //fetch branch sales
    fetchBranchSales: async (context, data) => {
      await context.dispatch("httpRequest", {
        url: "branchsales",
        data: { filterby: data.filterby, custom: data.custom ?? "" },
        caches: [{ key: "branchSales", value: "data" }],
      });
    },

    fetchBranchSalesChart: async (context, data) => {
      try {
        const formData = new FormData();
        formData.append("sesscode", context.getters.getUser.sesscode);
        formData.append("userid", context.getters.getUser.userid);
        formData.append("businessid", context.getters.getUser.business_id);
        formData.append("year", data.year);
        formData.append("env", "web");

        await fetch(
          `${BASE_URL}/saleschart`,
          {
            method: "POST",
            body: formData,
          }
        )
          .then((response) => response.json())
          .then((response) => {
            return context.commit("updateBranchSalesChart", response);
          })
          .catch((e) => console.log(e.message));
      } catch (e) {
        console.log(e.message);
      }
    },

    fetchBranchUsers: async (context) => {
      try {
        const formData = new FormData();
        formData.append("sesscode", context.getters.getUser.sesscode);
        formData.append("userid", context.getters.getUser.userid);
        formData.append("businessid", context.getters.getUser.business_id);
        formData.append("env", "web");

        await fetch(
          `${BASE_URL}/branchusers`,
          {
            method: "POST",
            body: formData,
          }
        )
          .then((response) => response.json())
          .then((response) => {
            if (response.status)
              return context.commit("updateBranchUsers", response);
          })
          .catch((e) => console.log(e.message));
      } catch (e) {
        console.log(e.message);
      }
    },

    fetchBranchTransactions: async (context, data) => {
      try {
        const formData = new FormData();
        formData.append("sesscode", context.getters.getUser.sesscode);
        formData.append("userid", context.getters.getUser.userid);
        formData.append("businessid", context.getters.getUser.business_id);
        formData.append("branchid", data.branchid);
        formData.append("filterby", data.filterby);
        formData.append("custom", data.custom ?? "");
        formData.append("env", "web");

        await fetch(
          `${BASE_URL}/mybranchtran`,
          {
            method: "POST",
            body: formData,
          }
        )
          .then((response) => response.json())
          .then((response) => {
            if (response.status)
              return context.commit("updateBranchTransactions", response);
          })
          .catch((e) => console.log(e.message));
      } catch (e) {
        console.log(e.message);
      }
    },

    fetchTeamBranchTransactions: async (context, data) => {
      try {
        const formData = new FormData();
        formData.append("sesscode", context.getters.getUser.sesscode);
        formData.append("userid", context.getters.getUser.userid);
        formData.append("businessid", context.getters.getUser.business_id);
        formData.append("branchid", data.branchid);
        formData.append("filterby", data.filterby);
        formData.append("custom", data.custom ?? "");
        formData.append("env", "web");

        await fetch(
          `${BASE_URL}/mybranchsales`,
          {
            method: "POST",
            body: formData,
          }
        )
          .then((response) => response.json())
          .then((response) => {
            if (response.status)
              return context.commit("updateTeamBranchSales", response);
          })
          .catch((e) => console.log(e.message));
      } catch (e) {
        console.log(e.message);
      }
    },

    fetchTvList: async (context) => {
      try {
        await fetch(
          `${BASE_URL}/tvtype`,
          {
            method: "GET",
          }
        )
          .then((response) => response.json())
          .then((response) => {
            if (response.status)
              return context.commit("updateTvList", response);
          })
          .catch((e) => console.log(e.message));
      } catch (e) {
        console.log(e.message);
      }
    },

    verifyIUC: async (context, data) => {
      try {
        const formData = new FormData();
        formData.append("sesscode", context.getters.getUser.sesscode);
        formData.append("userid", context.getters.getUser.userid);
        formData.append("businessid", context.getters.getUser.business_id);
        formData.append("tv", data.tv);
        formData.append("iuc", data.iuc);
        formData.append("env", "web");

        return await fetch(
          `${BASE_URL}/verifyiuc`,
          {
            method: "POST",
            body: formData,
          }
        );
      } catch (e) {
        console.log(e.message);
      }
    },

    vendCable: async (context, data) => {
      try {
        const formData = new FormData();
        formData.append("sesscode", context.getters.getUser.sesscode);
        formData.append("userid", context.getters.getUser.userid);
        formData.append("businessid", context.getters.getUser.business_id);
        formData.append("tv", data.tv);
        formData.append("iuc", data.iuc);
        formData.append("custname", data.custname);
        formData.append("custno", data.custno);
        formData.append("amount", data.amount);
        formData.append("pln", data.pln);
        formData.append("packageid", data.packageid);
        formData.append("transpin", data.transpin || data.pin || "");
        formData.append("ctoken", data.ctoken);
        formData.append("env", "web");

        return await fetch(
          `${BASE_URL}/buycable`,
          {
            method: "POST",
            body: formData,
          }
        );
      } catch (e) {
        console.log(e.message);
      }
    },

    vendElectricity: async (context, data) => {
      try {
        const formData = new FormData();
        formData.append("sesscode", context.getters.getUser.sesscode);
        formData.append("userid", context.getters.getUser.userid);
        formData.append("businessid", context.getters.getUser.business_id);
        formData.append("custname", data.custname);
        formData.append("vendtype", data.vendtype);
        formData.append("amount", data.amount);
        formData.append("provider", data.provider);
        formData.append("meterno", data.meterno);
        formData.append("maxvend", data.maxvend);
        formData.append("minvend", data.minvend);
        formData.append("pckgid", data.pckgid);
        formData.append("transpin", data.transpin || data.pin || "");
        formData.append("ctoken", data.ctoken);
        formData.append("env", "web");

        return await fetch(
          `${BASE_URL}/vend-elect`,
          {
            method: "POST",
            body: formData,
          }
        );
      } catch (e) {
        console.log(e.message);
      }
    },

    verifyMeter: async (context, data) => {
      try {
        const formData = new FormData();
        formData.append("sesscode", context.getters.getUser.sesscode);
        formData.append("userid", context.getters.getUser.userid);
        formData.append("businessid", context.getters.getUser.business_id);
        formData.append("provider", data.provider);
        formData.append("meterno", data.meterno);
        formData.append("vendtype", data.vendtype);
        formData.append("env", "web");

        return await fetch(
          `${BASE_URL}/verifyelect`,
          {
            method: "POST",
            body: formData,
          }
        );
      } catch (e) {
        console.log(e.message);
      }
    },

    fetchElectricityProvider: async (context) => {
      try {
        await fetch(
          `${BASE_URL}/electtype`,
          {
            method: "GET",
          }
        )
          .then((response) => response.json())
          .then((response) => {
            if (response.status)
              return context.commit("updateElectrictyProvider", response);
          })
          .catch((e) => console.log(e.message));
      } catch (e) {
        console.log(e.message);
      }
    },

    submitSuggestion: async (context, data) => {
      try {
        const formData = new FormData();
        formData.append("sesscode", context.getters.getUser.sesscode);
        formData.append("userid", context.getters.getUser.userid);
        formData.append("businessid", context.getters.getUser.business_id);
        formData.append("subject", data.subject);
        formData.append("message", data.suggestion);
        formData.append("env", "web");

        return await fetch(
          `${BASE_URL}/survey`,
          {
            method: "POST",
            body: formData,
          }
        );
      } catch (e) {
        console.log(e.message);
      }
    },

    fetchBranchStaff: async (context, data) => {
      try {
        const formData = new FormData();
        formData.append("sesscode", context.getters.getUser.sesscode);
        formData.append("userid", context.getters.getUser.userid);
        formData.append("businessid", context.getters.getUser.business_id);
        formData.append("branchid", data.branchid);
        formData.append("env", "web");

        await fetch(
          `${BASE_URL}/mybranchteam`,
          {
            method: "POST",
            body: formData,
          }
        )
          .then((response) => response.json())
          .then((response) => {
            if (response.status)
              return context.commit("updateBranchStaff", response);
          })
          .catch((e) => console.log(e.message));
      } catch (e) {
        console.log(e.message);
      }
    },

    fetchBranchDetails: async (context, data) => {
      try {
        const formData = new FormData();
        formData.append("sesscode", context.getters.getUser.sesscode);
        formData.append("userid", context.getters.getUser.userid);
        formData.append("businessid", context.getters.getUser.business_id);
        formData.append("branchid", data.branchid);
        formData.append("filterby", data.filterby);
        formData.append("custom", data.custom ?? "");
        formData.append("env", "web");

        await fetch(
          `${BASE_URL}/mybranch-overview`,
          {
            method: "POST",
            body: formData,
          }
        )
          .then((response) => response.json())
          .then((response) => {
            if (response.status)
              return context.commit("updateBranchDetails", response);
          })
          .catch((e) => console.log(e.message));
      } catch (e) {
        console.log(e.message);
      }
    },

    fetchPayroll: async (context) => {
      try {
        const formData = new FormData();
        formData.append("sesscode", context.getters.getUser.sesscode);
        formData.append("userid", context.getters.getUser.userid);
        formData.append("businessid", context.getters.getUser.business_id);
        formData.append("env", "web");

        await fetch(
          `${BASE_URL}/payroll`,
          {
            method: "POST",
            body: formData,
          }
        )
          .then((response) => response.json())
          .then((response) => {
            if (response.status)
              return context.commit("updatePayroll", response);
          })
          .catch((e) => console.log(e.message));
      } catch (e) {
        console.log(e.message);
      }
    },

    updateTeamRole: async (context, data) => {
      try {
        const formData = new FormData();
        formData.append("sesscode", context.getters.getUser.sesscode);
        formData.append("userid", context.getters.getUser.userid);
        formData.append("businessid", context.getters.getUser.business_id);
        formData.append("newrole", data.newrole);
        formData.append("teamid", data.teamid);
        formData.append("env", "web");

        return await fetch(
          `${BASE_URL}/managerole`,
          {
            method: "POST",
            body: formData,
          }
        );
      } catch (e) {
        console.log(e.message);
      }
    },

    fetchPayrollDetails: async (context, data) => {
      try {
        const formData = new FormData();
        formData.append("sesscode", context.getters.getUser.sesscode);
        formData.append("userid", context.getters.getUser.userid);
        formData.append("businessid", context.getters.getUser.business_id);
        formData.append("ref", data.ref);
        formData.append("env", "web");

        await fetch(
          `${BASE_URL}/viewpayroll`,
          {
            method: "POST",
            body: formData,
          }
        )
          .then((response) => response.json())
          .then((response) => {
            if (response.status)
              return context.commit("updatePayrollDetails", response);
          })
          .catch((e) => console.log(e.message));
      } catch (e) {
        console.log(e.message);
      }
    },

    toggleBranchTeam: async (context, data) => {
      try {
        const formData = new FormData();
        formData.append("sesscode", context.getters.getUser.sesscode);
        formData.append("userid", context.getters.getUser.userid);
        formData.append("businessid", context.getters.getUser.business_id);
        formData.append("statuscode", data.status);
        formData.append("teamid", data.teamid);
        formData.append("env", "web");

        return await fetch(
          `${BASE_URL}/holdteam`,
          {
            method: "POST",
            body: formData,
          }
        );
      } catch (e) {
        console.log(e.message);
      }
    },

    toggleAlert: async (context, data) => {
      try {
        const formData = new FormData();
        formData.append("sesscode", context.getters.getUser.sesscode);
        formData.append("userid", context.getters.getUser.userid);
        formData.append("businessid", context.getters.getUser.business_id);
        formData.append("sms", data.sms);
        formData.append("mail", data.mail);
        formData.append("env", "web");

        return await fetch(
          `${BASE_URL}/transalert-setting`,
          {
            method: "POST",
            body: formData,
          }
        );
      } catch (e) {
        console.log(e.message);
      }
    },

    fetchCashBranchTrans: async (context, data) => {
      try {
        const formData = new FormData();
        formData.append("sesscode", context.getters.getUser.sesscode);
        formData.append("userid", context.getters.getUser.userid);
        formData.append("businessid", context.getters.getUser.business_id);
        formData.append("branchid", data.branchid);
        formData.append("cashierid", data.teamid);
        formData.append("env", "web");

        await fetch(
          `${BASE_URL}/transbycashier`,
          {
            method: "POST",
            body: formData,
          }
        )
          .then((response) => response.json())
          .then((response) => {
            if (response.status)
              return context.commit("updateCashierBranchTrans", response);
          })
          .catch((e) => console.log(e.message));
      } catch (e) {
        console.log(e.message);
      }
    },

    fetchAlertSettings: async (context) => {
      try {
        const formData = new FormData();
        formData.append("sesscode", context.getters.getUser.sesscode);
        formData.append("userid", context.getters.getUser.userid);
        formData.append("businessid", context.getters.getUser.business_id);
        formData.append("env", "web");

        await fetch(
          `${BASE_URL}/alertstatus`,
          {
            method: "POST",
            body: formData,
          }
        )
          .then((response) => response.json())
          .then((response) => {
            if (response.status)
              return context.commit("updateAlertSettings", response);
          })
          .catch((e) => console.log(e.message));
      } catch (e) {
        console.log(e.message);
      }
    },

    submitPayroll: async (context, data) => {
      try {
        const formData = new FormData();
        formData.append("sesscode", context.getters.getUser.sesscode);
        formData.append("userid", context.getters.getUser.userid);
        formData.append("businessid", context.getters.getUser.business_id);
        formData.append("narration", data.narration);
        // formData.append("schedulemode", data.schedulemode);
        // formData.append("startdate", data.startdate);
        formData.append("data", data.data);
        formData.append("env", "web");
        return await fetch(
          `${BASE_URL}/addpayroll`,
          {
            method: "POST",
            body: formData,
          }
        );
      } catch (e) {
        console.log(e.message);
      }
    },

    updatePayroll: async (context, data) => {
      try {
        const formData = new FormData();
        formData.append("sesscode", context.getters.getUser.sesscode);
        formData.append("userid", context.getters.getUser.userid);
        formData.append("businessid", context.getters.getUser.business_id);
        formData.append("narration", data.narration);
        formData.append("reference", data.reference);
        formData.append("data", data.data);
        formData.append("env", "web");
        return await fetch(
          `${BASE_URL}/updpayrol`,
          {
            method: "POST",
            body: formData,
          }
        );
      } catch (e) {
        console.log(e.message);
      }
    },

    approvePayroll: async (context, data) => {
      try {
        const formData = new FormData();
        formData.append("sesscode", context.getters.getUser.sesscode);
        formData.append("userid", context.getters.getUser.userid);
        formData.append("businessid", context.getters.getUser.business_id);
        formData.append("schedulemode", data.schedulemode);
        formData.append("reference", data.reference);
        formData.append("startdate", data.startdate);
        formData.append("ctoken", data.ctoken);
        formData.append("env", "web");

        return await fetch(
          `${BASE_URL}/approvepayroll`,
          {
            method: "POST",
            body: formData,
          }
        );
      } catch (e) {
        console.log(e.message);
      }
    },
    moveTeam: async (context, data) => {
      try {
        const formData = new FormData();
        formData.append("sesscode", context.getters.getUser.sesscode);
        formData.append("userid", context.getters.getUser.userid);
        formData.append("businessid", context.getters.getUser.business_id);
        formData.append("newbranchid", data.newbranchid);
        formData.append("teamid", data.teamid);
        formData.append("env", "web");
        return await fetch(
          `${BASE_URL}/moveteam`,
          {
            method: "POST",
            body: formData,
          }
        );
      } catch (e) {
        console.log(e.message);
      }
    },

    exportPayroll: async (context, data) => {
      try {
        const formData = new FormData();
        formData.append("sesscode", context.getters.getUser.sesscode);
        formData.append("userid", context.getters.getUser.userid);
        formData.append("businessid", context.getters.getUser.business_id);
        formData.append("reference", data.reference);
        formData.append("env", "web");
        return await fetch(
          `${BASE_URL}/exportpayroll`,
          {
            method: "POST",
            body: formData,
          }
        );
      } catch (e) {
        console.log(e.message);
      }
    },

    fetchTeamList: async (context) => {
      try {
        const formData = new FormData();
        formData.append("sesscode", context.getters.getUser.sesscode);
        formData.append("userid", context.getters.getUser.userid);
        formData.append("businessid", context.getters.getUser.business_id);
        formData.append("env", "web");

        await fetch(
          `${BASE_URL}/teamlist`,
          {
            method: "POST",
            body: formData,
          }
        )
          .then((response) => response.json())
          .then((response) => {
            if (response.status)
              return context.commit("updateTeamList", response);
          })
          .catch((e) => console.log(e.message));
      } catch (e) {
        console.log(e.message);
      }
    },

    fetchPendingTeamList: async (context) => {
      try {
        const formData = new FormData();
        formData.append("sesscode", context.getters.getUser.sesscode);
        formData.append("userid", context.getters.getUser.userid);
        formData.append("businessid", context.getters.getUser.business_id);
        formData.append("env", "web");

        await fetch(
          `${BASE_URL}/pending-invites`,
          {
            method: "POST",
            body: formData,
          }
        )
          .then((response) => response.json())
          .then((response) => {
            if (response.status)
              return context.commit("updatePendingTeamList", response);
          })
          .catch((e) => console.log(e.message));
      } catch (e) {
        console.log(e.message);
      }
    },

    resendOTP: async (context, data) => {
      try {
        const formData = new FormData();
        formData.append("sesscode", context.getters.getUser.sesscode);
        formData.append("userid", context.getters.getUser.userid);
        formData.append("businessid", context.getters.getUser.business_id);
        formData.append("type", data.type);
        formData.append("bvn", data.bvn);
        formData.append("env", "web");

        return await fetch(
          `${BASE_URL}/resendotp`,
          {
            method: "POST",
            body: formData,
          }
        );
      } catch (e) {
        console.log(e.message);
      }
    },

    fetchProdCategories: async (context) => {
      try {
        const formData = new FormData();
        formData.append("sesscode", context.getters.getUser.sesscode);
        formData.append("userid", context.getters.getUser.userid);
        formData.append("businessid", context.getters.getUser.business_id);
        formData.append("env", "web");

        await fetch(
          `${BASE_URL}/store/storecategory`,
          {
            method: "GET",
          }
        )
          .then((response) => response.json())
          .then((response) => {
            if (response.status)
              return context.commit("updateProdCategories", response);
          })
          .catch((e) => console.log(e.message));
      } catch (e) {
        console.log(e.message);
      }
    },

    addProduct: async (context, data) => {
      try {
        const formData = new FormData();
        formData.append("sesscode", context.getters.getUser.sesscode);
        formData.append("userid", context.getters.getUser.userid);
        formData.append("businessid", context.getters.getUser.business_id);
        formData.append("images", data.images);
        formData.append("productname", data.productname);
        formData.append("price", data.price);
        formData.append("categoryid", data.categoryid);
        formData.append("desc", data.desc);
        formData.append("quantity", data.quantity);
        formData.append("currency", data.currency);
        formData.append("env", "web");

        return await fetch(
          `${BASE_URL}/store/addstoreproduct`,
          {
            method: "POST",
            body: formData,
          }
        );
      } catch (e) {
        console.log(e.message);
      }
    },

    storeRequest: async (context, data) => {
      try {
        const formData = new FormData();
        formData.append("sesscode", context.getters.getUser.sesscode);
        formData.append("userid", context.getters.getUser.userid);
        formData.append("businessid", context.getters.getUser.business_id);
        formData.append("checkedterm", data.checkedterm);
        formData.append("requestbody", data.requestbody);
        formData.append("env", "web");

        return await fetch(
          `${BASE_URL}/store/requestmall`,
          {
            method: "POST",
            body: formData,
          }
        );
      } catch (e) {
        console.log(e.message);
      }
    },

    fetchProducts: async (context) => {
      try {
        const formData = new FormData();
        formData.append("sesscode", context.getters.getUser.sesscode);
        formData.append("userid", context.getters.getUser.userid);
        formData.append("businessid", context.getters.getUser.business_id);
        formData.append("env", "web");

        await fetch(
          `${BASE_URL}/store/myproducts`,
          {
            method: "POST",
            body: formData,
          }
        )
          .then((response) => response.json())
          .then((response) => {
            if (response.status)
              return context.commit("updateProducts", response);
          })
          .catch((e) => console.log(e.message));
      } catch (e) {
        console.log(e.message);
      }
    },

    fetchStoreOrder: async (context) => {
      try {
        const formData = new FormData();
        formData.append("sesscode", context.getters.getUser.sesscode);
        formData.append("userid", context.getters.getUser.userid);
        formData.append("businessid", context.getters.getUser.business_id);
        formData.append("env", "web");

        await fetch(
          `${BASE_URL}//store/storeorders`,
          {
            method: "POST",
            body: formData,
          }
        )
          .then((response) => response.json())
          .then((response) => {
            if (response.status)
              return context.commit("updateStoreOrder", response);
          })
          .catch((e) => console.log(e.message));
      } catch (e) {
        console.log(e.message);
      }
    },

    //fetch prices
    fetchSubPrices: async (context) => {
      try {
        const formData = new FormData();
        formData.append("sesscode", context.getters.getUser.sesscode);
        formData.append("userid", context.getters.getUser.userid);
        formData.append("businessid", context.getters.getUser.business_id);
        formData.append("env", "web");
        await fetch(`${BASE_URL}/subcompare`, {
          method: "POST",
          body: formData,
        })
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              context.commit("updateDefaultPrices", response.defaultfee);
              context.commit("updatePremiumPrices", response.premium_fee);
              return;
            }
          })
          .catch((e) => console.log(e.message));
      } catch (e) {
        console.log(e.message);
      }
    },

    //
    premiumSubscribe: async (context, data) => {
      try {
        const formData = new FormData();
        formData.append("sesscode", context.getters.getUser.sesscode);
        formData.append("userid", context.getters.getUser.userid);
        formData.append("businessid", context.getters.getUser.business_id);
        formData.append("plan", data.plan);
        formData.append("env", "web");
        formData.append("type", "premium");
        formData.append("renewal", data.renewal);

        return await fetch(`${BASE_URL}/upgradepckg`, {
          method: "POST",
          body: formData,
        });
      } catch (e) {
        console.log(e.message);
      }
    },

    cancelSubscription: async (context) => {
      try {
        const formData = new FormData();
        formData.append("sesscode", context.getters.getUser.sesscode);
        formData.append("userid", context.getters.getUser.userid);
        formData.append("businessid", context.getters.getUser.business_id);
        formData.append("env", "web");
        return await fetch(`${BASE_URL}/cancelsub`, {
          method: "POST",
          body: formData,
        });
      } catch (e) {
        console.log(e.message);
      }
    },

    /**
     * =============================
     * NEW ONBOARDING API
     * =============================
     */

    //log user in
    login: async (context, data) => {
      try {
        let formData = new FormData();
        formData.append("email", data.email);
        formData.append("pword", data.password);
        formData.append("fcmtoken", data.fcmtoken);
        formData.append("env", "web");
        formData.append("device_token", getOrCreateDeviceToken());

        return await fetch(`${BASE_URL}/signin`, {
          method: "POST", // or 'PUT'
          body: formData,
        });
      } catch (e) {
        return {
          data: {
            msg: e.msg,
            status: false,
          },
        };
      }
    },

    //register new users
    onboardNewUser: async (context, data) => {
      try {
        const formData = new FormData();
        formData.append("busname", data.busname);
        formData.append("staffvol", data.staffvol);
        formData.append("monthlysales", data.monthlysales);
        formData.append("fname", data.fname);
        formData.append("sname", data.sname);
        formData.append("phn", data.phone);
        formData.append("email", data.email);
        formData.append("pwd", data.password);
        formData.append("whorefer", data.whorefer);
        formData.append("env", "web");
        formData.append("referby", data.refcode);
        return await fetch(`${BASE_URL}/regstart`, {
          method: "POST",
          body: formData,
        });
      } catch (e) {
        console.log(e.message);
      }
    },

    onboardExistingUser: async (context, data) => {
      try {
        const formData = new FormData();
        formData.append("busname", data.busname);
        formData.append("staffvol", data.staffvol);
        formData.append("monthlysales", data.monthlysales);
        formData.append("email", data.email);
        formData.append("pwd", data.password);
        formData.append("env", "web");
        formData.append("referby", data.refcode);
        return await fetch(`${BASE_URL}/regstartexist`, {
          method: "POST",
          body: formData,
        });
      } catch (e) {
        console.log(e.message);
      }
    },

    onboardingExistingUserLogin: async (context, data) => {
      const formData = new FormData();
      formData.append("email", data.email);
      formData.append("pword", data.password);
      formData.append("env", "web");
      return await safeJsonFetch(
        `${BASE_URL}/onboardsignin?guest=true`,
        {
          method: "POST",
          body: formData,
        },
        "Unable to continue onboarding right now. Please retry."
      );
    },

    onboardingExistingUserBusiness: async (context, data) => {
      const formData = new FormData();
      formData.append("authtoken", data.authtoken || "");
      formData.append("director_fname", data.director_fname || "");
      formData.append("director_lname", data.director_lname || "");
      formData.append("director_email", data.director_email || "");
      formData.append("director_pwrd", data.director_pwrd || "");
      formData.append("director_phone", data.director_phone || "");
      formData.append("bizname", data.bizname || "");
      formData.append("bizemail", data.bizemail || "");
      formData.append("bizphone", data.bizphone || "");
      formData.append("bizregno", data.bizregno || "");
      formData.append("bizaddr", data.bizaddr || "");
      formData.append("bizstate", data.bizstate || "");
      formData.append("bizcity", data.bizcity || "");
      formData.append("biz_desc", data.biz_desc || "");
      formData.append("country", data.country || "");
      formData.append("currency", data.currency || "");
      formData.append("dialcode", data.dialcode || "");
      formData.append("referby", data.referby || "");
      formData.append("env", "web");
      return await safeJsonFetch(
        `${BASE_URL}/onboardbus?guest=true`,
        {
          method: "POST",
          body: formData,
        },
        "Unable to submit this business registration right now. Please retry."
      );
    },

    //verify bvn
    verifyOnboardBvn: async (context, data) => {
      try {
        const regMeta = resolveRegOnboardingMeta(context.state, context.getters);
        const formData = new FormData();
        formData.append("sesscode", regMeta.sesscode || "");
        formData.append("businessid", regMeta.business_id || "");
        formData.append("userid", regMeta.userid || "");
        formData.append("bvnn", data.bvn);
        formData.append("env", "web");
        return await fetch(`${BASE_URL}/bvverify`, {
          method: "POST",
          body: formData,
        });
      } catch (e) {
        console.log(e.message);
      }
    },

    //resend otp
    resendOnboardOtp: async (context, data) => {
      try {
        const regMeta = resolveRegOnboardingMeta(context.state, context.getters);
        const formData = new FormData();
        formData.append("sesscode", regMeta.sesscode || "");
        formData.append("businessid", regMeta.business_id || "");
        formData.append("userid", regMeta.userid || "");
        formData.append("bvn", data.bvn);
        formData.append("type", data.type);
        formData.append("env", "web");
        return await fetch(`${BASE_URL}/resendregotp`, {
          method: "POST",
          body: formData,
        });
      } catch (e) {
        console.log(e.message);
      }
    },

    //verify bvn
    confirmOnboardBvn: async (context, data) => {
      try {
        const regMeta = resolveRegOnboardingMeta(context.state, context.getters);
        const formData = new FormData();
        formData.append("sesscode", regMeta.sesscode || "");
        formData.append("businessid", regMeta.business_id || "");
        formData.append("bvnn", data.bvnn || data.nin || "");
        formData.append("nin", data.nin || data.bvnn || "");
        formData.append("userid", regMeta.userid || "");
        formData.append("otp", data.otp);
        formData.append("env", "web");
        return await fetch(`${BASE_URL}/bvotp`, {
          method: "POST",
          body: formData,
        });
      } catch (e) {
        console.log(e.message);
      }
    },

    onboardingVerifyIdentity: async (context, data) => {
      try {
        const regMeta = resolveRegOnboardingMeta(context.state, context.getters);
        const formData = new FormData();
        formData.append("sesscode", regMeta.sesscode || "");
        formData.append("businessid", regMeta.business_id || "");
        formData.append("userid", regMeta.userid || "");
        formData.append("env", "web");
        if (String(data?.vertype || "").toLowerCase() === "nin") {
          formData.append("nin", data.identifier);
          return await fetch(`${BASE_URL}/ninverification`, {
            method: "POST",
            body: formData,
          });
        }
        formData.append("bvnn", data.identifier);
        return await fetch(`${BASE_URL}/bvverification`, {
          method: "POST",
          body: formData,
        });
      } catch (e) {
        console.log(e.message);
      }
    },

    onboardingSendIdentityOtp: async (context, data) => {
      try {
        const regMeta = resolveRegOnboardingMeta(context.state, context.getters);
        const formData = new FormData();
        formData.append("sesscode", regMeta.sesscode || "");
        formData.append("businessid", regMeta.business_id || "");
        formData.append("userid", regMeta.userid || "");
        formData.append("method", data.method || "");
        formData.append("channeltype", data.channeltype || "sms");
        formData.append("vertype", String(data.vertype || "BVN").toUpperCase());
        formData.append("env", "web");
        return await fetch(`${BASE_URL}/sendverotp`, {
          method: "POST",
          body: formData,
        });
      } catch (e) {
        console.log(e.message);
      }
    },

    onboardingConfirmIdentityOtp: async (context, data) => {
      try {
        const regMeta = resolveRegOnboardingMeta(context.state, context.getters);
        const formData = new FormData();
        formData.append("sesscode", regMeta.sesscode || "");
        formData.append("businessid", regMeta.business_id || "");
        formData.append("userid", regMeta.userid || "");
        formData.append("bvnn", data.bvnn || data.nin || "");
        formData.append("nin", data.nin || data.bvnn || "");
        formData.append("otpcode", data.otpcode || "");
        formData.append("vertype", String(data.vertype || "BVN").toUpperCase());
        formData.append("env", "web");
        return await fetch(`${BASE_URL}/verbvotp`, {
          method: "POST",
          body: formData,
        });
      } catch (e) {
        console.log(e.message);
      }
    },

    onboardingResendIdentityOtp: async (context, data) => {
      try {
        const regMeta = resolveRegOnboardingMeta(context.state, context.getters);
        const formData = new FormData();
        formData.append("businessid", regMeta.business_id || "");
        formData.append("userid", regMeta.userid || "");
        formData.append("type", data.type || "sms");
        formData.append("vertype", String(data.vertype || "BVN").toUpperCase());
        formData.append("env", "web");
        return await fetch(`${BASE_URL}/resendbvotp`, {
          method: "POST",
          body: formData,
        });
      } catch (e) {
        console.log(e.message);
      }
    },

    //business information
    onboardingBusInfo: async (context, data) => {
      try {
        const regMeta = resolveRegOnboardingMeta(context.state, context.getters);
        const formData = new FormData();
        formData.append("businessid", regMeta.business_id || "");
        formData.append("userid", regMeta.userid || "");
        formData.append("desc", data.desc);
        formData.append("adr", data.address);
        formData.append("lga", data.lga);
        formData.append("state", data.state);
        formData.append("city", data.city);
        formData.append("busemail", data.busemail);
        formData.append("busphone", data.busphone);
        formData.append("cacreg", data.cacreg);
        formData.append("busname1", data.busname1);
        formData.append("busname2", data.busname2);
        formData.append("busname3", data.busname3);
        formData.append("prefer_bname", data.prefer_bname);
        formData.append("env", "web");
        return await fetch(`${BASE_URL}/businfo`, {
          method: "POST",
          body: formData,
        });
      } catch (e) {
        console.log(e.message);
      }
    },

    //doc upload
    onboardingDocUpload: async (context, data) => {
      try {
        const regMeta = resolveRegOnboardingMeta(context.state, context.getters);
        const formData = new FormData();
        formData.append("businessid", regMeta.business_id || "");
        formData.append("userid", regMeta.userid || "");
        formData.append("env", "web");
        formData.append(data.mode, data.file);
        return await fetch(`${BASE_URL}/docupload`, {
          method: "POST",
          body: formData,
        });
      } catch (e) {
        console.log(e.message);
      }
    },

    //registration overview
    onboardingOverview: async (context) => {
      try {
        const regMeta = resolveRegOnboardingMeta(context.state, context.getters);
        const formData = new FormData();
        formData.append("businessid", regMeta.business_id || "");
        formData.append("userid", regMeta.userid || "");
        formData.append("env", "web");
        return await fetch(`${BASE_URL}/reg-overview`, {
          method: "POST",
          body: formData,
        });
      } catch (e) {
        console.log(e.message);
      }
    },

    //onboarding conctact sale
    onboardingSaleForm: async (context, data) => {
      try {
        const formData = new FormData();
        formData.append("email", data.email);
        formData.append("fname", data.fname);
        formData.append("sname", data.sname);
        formData.append("phn", data.phone);
        formData.append("company_name", data.company_name);
        formData.append("company_adr", data.company_adr);
        formData.append("company_post", data.company_post);
        formData.append("company_desc", data.company_desc);
        formData.append("env", "web");
        return await fetch(`${BASE_URL}/contactsale`, {
          method: "POST",
          body: formData,
        });
      } catch (e) {
        console.log(e.message);
      }
    },

    //check invite
    checkInvite: async (context, data) => {
      try {
        const formData = new FormData();
        formData.append("busid", data.busid);
        formData.append("ivtoken", data.token);
        return await fetch(`${BASE_URL}/checkinvite`, {
          method: "POST",
          body: formData,
        });
      } catch (e) {
        console.log(e.message);
      }
    },

    //accept invite
    acceptInvite: async (context, data) => {
      try {
        const formData = new FormData();
        formData.append("busid", data.busid);
        formData.append("ivtoken", data.token);
        formData.append("firstname", data.fname);
        formData.append("lastname", data.lname);
        formData.append("pww", data.password);
        formData.append("phn", data.phone);
        return await fetch(`${BASE_URL}/acceptinvite`, {
          method: "POST",
          body: formData,
        });
      } catch (e) {
        console.log(e.message);
      }
    },


    //fetch POS terminal access governance details
    fetchPosTerminalAccess: async (context) => {
      const response = await context.dispatch("httpRequest", {
        url: "pos-terminal-access",
        rawUrl: resolvePosRequestRawUrl("pos-terminal-access"),
      });

      if (response && response.status) {
        context.commit("updateCache", {
          key: "posTerminalAccess",
          value: response,
        });
      }
      return response;
    },

    //submit POS terminal request
    submitPosTerminalRequest: async (context, data) => {
      return await context.dispatch("httpRequest", {
        url: "pos-terminal-request",
        rawUrl: resolvePosRequestRawUrl("pos-terminal-request"),
        data,
      });
    },

    //fetch POS terminal request history
    fetchPosTerminalRequests: async (context, data = {}) => {
      const response = await context.dispatch("httpRequest", {
        url: "pos-terminal-requests",
        rawUrl: resolvePosRequestRawUrl("pos-terminal-requests"),
        data,
      });

      if (response && response.status) {
        context.commit("updateCache", {
          key: "posTerminalRequests",
          value: response.data || [],
        });
      }
      return response;
    },

    // POS dashboard summary
    fetchPOSDash: async (context) => {
      const response = await context.dispatch("httpRequest", {
        url: "pos-dashboard",
        rawUrl: resolvePosApiRawUrl("bizposdash.php"),
        data: {},
      });
      if (response) {
        context.commit("updatePOSDash", response);
      }
      return response;
    },

    // POS agents
    fetchAgents: async (context) => {
      const response = await context.dispatch("httpRequest", {
        url: "pos-agents",
        rawUrl: resolvePosApiRawUrl("myagents.php"),
        data: {},
      });
      if (response) {
        context.commit("updateAgents", response);
      }
      return response;
    },
    updateAgentStatus: async (context, data) => {
      const formData = buildPosFormData(context, data);
      return await fetch(resolvePosApiRawUrl("myagents.php"), {
        method: "POST",
        body: formData,
      });
    },
    addPOSAgent: async (context, data) => {
      const formData = buildPosFormData(context, data);
      return await fetch(resolvePosApiRawUrl("addagent.php"), {
        method: "POST",
        body: formData,
      });
    },
    genPosAgentAcct: async (context, data) => {
      const formData = buildPosFormData(context, data);
      return await fetch(resolvePosApiRawUrl("genposagent-acct.php"), {
        method: "POST",
        body: formData,
      });
    },

    // POS terminals
    fetchTerminals: async (context) => {
      const response = await context.dispatch("httpRequest", {
        url: "pos-terminals",
        rawUrl: resolvePosApiRawUrl("myterminals.php"),
        data: {},
      });
      if (response) {
        context.commit("updateTerminals", response);
      }
      return response;
    },
    addPOSTerminal: async (context, data) => {
      const formData = buildPosFormData(context, data);
      return await fetch(resolvePosApiRawUrl("addterminal.php"), {
        method: "POST",
        body: formData,
      });
    },
    assignTerminal: async (context, data) => {
      const formData = buildPosFormData(context, data);
      return await fetch(resolvePosApiRawUrl("assignpos.php"), {
        method: "POST",
        body: formData,
      });
    },
    revokeTerminal: async (context, data) => {
      const formData = buildPosFormData(context, data);
      return await fetch(resolvePosApiRawUrl("revoketerminal.php"), {
        method: "POST",
        body: formData,
      });
    },

    // POS transactions & reporting
    fetchPOSTransactions: async (context, data = {}) => {
      const response = await context.dispatch("httpRequest", {
        url: "pos-transactions",
        rawUrl: resolvePosApiRawUrl("bizpostrans.php"),
        data,
      });
      if (response) {
        context.commit("updatePOSTransactions", response);
      }
      return response;
    },

    // POS fees & settings
    fetchBizFees: async (context) => {
      const response = await context.dispatch("httpRequest", {
        url: "pos-fees",
        rawUrl: resolvePosApiRawUrl("feesettings_biz.php"),
        data: { action: "fetch_fees" },
      });
      if (response) {
        context.commit("updateBizFees", response);
      }
      return response;
    },
    submitBizFees: async (context, data) => {
      const formData = buildPosFormData(context, data);
      return await fetch(resolvePosApiRawUrl("feesettings_biz.php"), {
        method: "POST",
        body: formData,
      });
    },

    httpRequest: async (context, payload) => {
      try {
        let formData;
        const payloadData = payload.data || {};
        const useProvidedFormData =
          payload.isFormData && payloadData instanceof FormData;

        if (useProvidedFormData) {
          formData = payloadData;
        } else {
          formData = new FormData();
          formData.append("sesscode", context.getters.getUser.sesscode);
          formData.append("userid", context.getters.getUser.userid);
          formData.append("businessid", context.getters.getUser.business_id);
          formData.append("env", "web");

          if (payloadData instanceof FormData) {
            for (const [key, value] of payloadData.entries()) {
              formData.append(key, value);
            }
          } else {
            for (const key in payloadData) {
              formData.append(key, payloadData[key]);
            }
          }
        }

        const route = String(payload.url || payload.rawUrl || "")
          .split("?")[0]
          .split("/")
          .filter(Boolean)
          .pop();
        const isAbsoluteUrl =
          typeof payload.url === "string" &&
          /^https?:\/\//i.test(payload.url);
        const requestUrl =
          payload.rawUrl ??
          (route === "raffle"
            ? "https://api.oneappgo.com/raffle-public.php?fragment=screen"
            : (isAbsoluteUrl ? payload.url : `${BASE_URL}/${payload.url}`));

        const res = await fetch(requestUrl, {
          method: "POST",
          body: formData,
          headers: payload.headers,
        });
        const response = await res.json().then((resp) => {
          return resp;
        });

        //update cache
        if (response.status) {
          if (payload.caches) {
            payload.caches.forEach((cache) => {
              context.commit("updateCache", {
                key: cache.key,
                value: response[cache.value],
              });
            });
          }
        }
        return response;
      } catch (e) {
        console.log(e);
      }
    },

    //fetch user account details
    fetchDailyGraph: async (context) => {
      await context.dispatch("httpRequest", {
        url: "graphtrans",
        caches: [{ key: "dailygraph", value: "data" }],
      });
    },

    //fetch sub accounts
    fetchSubAccounts: async (context) => {
      await context.dispatch("httpRequest", {
        url: "subaccounts",
        caches: [{ key: "subaccounts", value: "data" }],
      });
    },

    //fetch payout offer
    fetchPayoutOffer: async (context) => {
      await context
        .dispatch("httpRequest", {
          url: "payout-offer",
        })
        .then((response) => {
          if (response.status) {
            context.commit("updateCache", {
              key: "payoutOffer",
              value: response,
            });
          }
        });
    },

    //fetch payout history
    fetchPayoutHistory: async (context) => {
      await context.dispatch("httpRequest", {
        url: "loanhistory",
        caches: [{ key: "loanhistory", value: "data" }],
      });
    },

    // fetch facility summary card data
    fetchFacilitySummary: async (context) => {
      const user = context.getters.getUser || {};
      const cached = readFacilitySummaryCache(user);
      const hasLiveSummary =
        context.getters.getCache &&
        context.getters.getCache.facilitySummary &&
        Object.keys(context.getters.getCache.facilitySummary).length > 0;
      if (cached && !hasLiveSummary) {
        context.commit("updateCache", {
          key: "facilitySummary",
          value: cached.data || {},
        });
        context.commit("updateCache", {
          key: "facilityFeature",
          value:
            cached.feature || {
              enabled: true,
              web_enabled: true,
              credit_score_enabled: true,
            },
        });
      }
      await context
        .dispatch("httpRequest", {
          url: "facility-summary",
        })
        .then((response) => {
          if (response && response.status) {
            const data = response.data || {};
            const feature =
              (response.data && response.data.feature) || {
                enabled: true,
                web_enabled: true,
                credit_score_enabled: true,
              };
            context.commit("updateCache", {
              key: "facilitySummary",
              value: data,
            });
            context.commit("updateCache", {
              key: "facilityFeature",
              value: feature,
            });
            persistFacilitySummaryCache(user, data, feature);
          } else if (response && response.code === "feature_disabled") {
            context.commit("updateCache", {
              key: "facilitySummary",
              value: {},
            });
            context.commit("updateCache", {
              key: "facilityFeature",
              value: (response.data && response.data.feature) || { enabled: false, web_enabled: false, credit_score_enabled: false },
            });
            clearFacilitySummaryCache(user);
          }
          return response;
        });
    },

    // fetch facility details page data
    fetchFacilityDetails: async (context) => {
      await context
        .dispatch("httpRequest", {
          url: "facility-details",
        })
        .then((response) => {
          if (response && response.status) {
            context.commit("updateCache", {
              key: "facilityDetails",
              value: response.data || {},
            });
            context.commit("updateCache", {
              key: "facilityFeature",
              value: (response.data && response.data.feature) || { enabled: true, web_enabled: true, credit_score_enabled: true },
            });
          } else if (response && response.code === "feature_disabled") {
            context.commit("updateCache", {
              key: "facilityDetails",
              value: {},
            });
            context.commit("updateCache", {
              key: "facilityFeature",
              value: (response.data && response.data.feature) || { enabled: false, web_enabled: false, credit_score_enabled: false },
            });
          } else {
            context.commit("updateCache", {
              key: "facilityDetails",
              value: {},
            });
          }
          return response;
        });
    },

    // submit facility request (monthly/quarterly)
    submitFacilityRequest: async (context, data) => {
      const payload = { ...(data || {}) };
      if (!payload.idempotency_key) {
        payload.idempotency_key = `facility-${Date.now()}-${Math.floor(Math.random() * 1000000)}`;
      }
      return await context.dispatch("httpRequest", {
        url: "facility-request",
        data: payload,
      });
    },

    // precheck guarantor eligibility before final facility submit
    precheckFacilityGuarantor: async (context, data) => {
      return await context.dispatch("httpRequest", {
        url: "facility-guarantor-precheck",
        data,
      });
    },

    // merchant offer response: accept/negotiate
    submitFacilityOfferResponse: async (context, data) => {
      return await context.dispatch("httpRequest", {
        url: "facility-offer-response",
        data,
      });
    },

    //fetch trans limit
    fetchTransLimit: async (context) => {
      await context.dispatch("httpRequest", {
        url: "translimit",
        caches: [{ key: "translimit", value: "data" }],
      });
    },

    //fetch events
    fetchEvents: async (context) => {
      await context.dispatch("httpRequest", {
        url: "eventlist",
        caches: [{ key: "myevents", value: "eventlist" }],
      });
    },

    //fetch event ticket
    fetchEventTicket: async (context) => {
      await context.dispatch("httpRequest", {
        url: "tickettype",
        data: { eventid: context.getters.getCache.selectedEvent?.eventid },
        caches: [{ key: "eventtickets", value: "ticketlist" }],
      });
    },
  },
};

export default user;
